import {
	FC,
	Fragment,
	ReactNode,
	createContext,
	useCallback,
	useContext,
	useMemo,
	useState,
} from 'react';

import {
	Drawer,
	DrawerHead,
	KV,
	Section,
	StatusChip,
	Verified,
} from 'views/design-system';

import { Wrapper } from '@googlemaps/react-wrapper';
import { REACT_APP_GOOGLE_MAP_SECRET_TOKEN as MAP_TOKEN } from 'envs';
import { GOOGLE_API } from 'constant';
import { MapComponent } from '../components/google-map/map';

import { STEP_FALLBACK, dayTime, toChain, toVerdict } from './derive';
import { Signatory } from '../constants';
import { useSessionDrawer } from './use-session-drawer';

/* ------------------------------------------------------------- unwrapping */

/**
 * Vendor payloads wrap every field as {value} | {error} | a bare value, and
 * theKYB answers an unsupported field with the literal "MISSING_KEY". All three
 * mean "nothing to show", and a row with nothing to show is dropped rather than
 * printed as an empty cell.
 */
const val = (field: any): string | null => {
	if (field === null || field === undefined) return null;
	if (typeof field === 'string' || typeof field === 'number') {
		const s = String(field).trim();
		return !s || s === 'MISSING_KEY' ? null : s;
	}
	if (typeof field === 'boolean') return field ? 'Yes' : 'No';
	if (Array.isArray(field)) return field.length ? field.join(', ') : null;
	if (field.error && field.value === undefined) return null;
	return val(field.value);
};

const isVerified = (field: any) => !!field?.verified;

const OVERRIDES: Record<string, string> = {
	ssn: 'SSN',
	idNumber: 'ID Number',
	idType: 'ID Type',
	dateOfBirth: 'Date Of Birth',
	dateOfIssue: 'Date Of Issue',
	dateOfExpiry: 'Date Of Expiry',
	issuingState: 'Issuing State',
	zip: 'Zip Code',
	basicInfo: 'Basic Info',
	document: 'Document',
	address: 'User Provided Address',
	breakdown: 'Breakdown',
	tin: 'TIN',
	fein: 'FEIN',
};

/** camelCase → Title Case, with the acronyms the product actually uses. */
const pretty = (key: string) =>
	OVERRIDES[key] ??
	key
		.replace(/([A-Z])/g, ' $1')
		.replace(/^./, c => c.toUpperCase())
		.trim();

/* ------------------------------------------------------------- activity */

/**
 * A status change is an activity like any other — `type: 'status'` with
 * `metadata.from` and `metadata.to`. The sv2 drawer only ever printed
 * `metadata.data`, which a status change leaves EMPTY, so every decision landed
 * in the log as a blank line under a username. The old details page spelled it
 * out ("has changed the status of KYC from Under Review to Approved"), and that
 * is the wording kept here.
 */
const STATUS_LABEL: Record<string, string> = {
	completed: 'Approved',
	approved: 'Approved',
	processing: 'Under Review',
	rejected: 'Rejected',
	failed: 'Rejected',
	onhold: 'On Hold',
	pending: 'Pending',
};

/** reuse the legend's own chip classes so the log reads in the same language */
const STATUS_TONE: Record<string, string> = {
	completed: 'ok',
	approved: 'ok',
	processing: 'rev',
	rejected: 'rej',
	failed: 'rej',
	onhold: 'rev',
};

const statusChip = (v?: string) => (
	<span className={`vch ${STATUS_TONE[v ?? ''] ?? 'ns'}`}>
		{STATUS_LABEL[v ?? ''] ?? (v ? pretty(v) : 'Not set')}
	</span>
);

const stepLabel = (stepId?: string) =>
	STEP_FALLBACK[stepId ?? ''] ?? (stepId ? pretty(stepId) : 'this step');

/* ------------------------------------------------------------- lightbox */

/**
 * An ID has to be READ to be checked, and at the 168px the drawer gives it a
 * licence's fine print and a passport's MRZ are legible to nobody. Clicking a
 * document opens it full-window at its own aspect ratio.
 *
 * Module-level context rather than prop-drilling: the doc boxes are three
 * components deep inside sections that otherwise take only a payload.
 */
const LightboxCtx = createContext<
	((src: string, label: string, kind?: 'image' | 'video') => void) | null
>(
	null
);

/* --------------------------------------------------- compliance controls */

/**
 * The per-step decision the old details page made from a dropdown beside each
 * section (views/sessions/components/action.tsx + Actions in ../constants.tsx).
 * The sv2 drawer shipped read-only, so a compliance agent could see a step but
 * not act on it. Same three values, same endpoint, same activity record.
 */
/** What pipelines.api's session-reports validator accepts. */
const DOWNLOAD_FORMATS: { label: string; value: string }[] = [
	{ label: 'PDF (.pdf)', value: 'pdf' },
	{ label: 'Spreadsheet (.xlsx)', value: 'xlsx' },
	{ label: 'CSV (.csv)', value: 'csv' },
];

const STATUS_OPTIONS: { label: string; value: string }[] = [
	{ label: 'Approved', value: 'completed' },
	{ label: 'Under Review', value: 'processing' },
	{ label: 'Rejected', value: 'rejected' },
];

/**
 * The session's stored compliance decision, back in the option values above.
 *
 * pipelines.api records a compliance agent's verdict in its own vocabulary
 * (`complianceApproval.decision`) precisely BECAUSE it is not a step status —
 * the engine and a human write the same 'completed' into the same node field,
 * which is the ambiguity that record exists to remove. This maps it back for
 * display only, so the bar can show what was decided without the two meanings
 * collapsing again in the payload.
 */
const DECISION_STATUS: Record<string, string> = {
	approved: 'completed',
	underReview: 'processing',
	rejected: 'rejected',
};

interface IStepAction {
	stepId: string;
	nodeId?: string;
	status: string;
	busy: boolean;
	onChange: (to: string) => void;
}

/**
 * Its own bar at the top of the step it belongs to, not a control tucked into
 * the section header — this is the decision a compliance agent opened the
 * session to make, and in the header it read as metadata. The bar takes the
 * colour of the status it currently holds, so the drawer can be scanned for
 * what still needs one.
 */
const StepStatus: FC<{ action?: IStepAction }> = ({ action }) => {
	if (!action) return null;
	const tone = STATUS_TONE[action.status] ?? 'ns';
	return (
		<div className={`decision decision--${tone}`}>
			<span className="decision__lbl">Decision</span>
			<select
				className="decision__sel"
				value={action.status}
				disabled={action.busy}
				onChange={e => action.onChange(e.target.value)}
			>
				{/* a step nobody has ruled on yet — not selectable, so it cannot be
					submitted back as a status */}
				<option value="" disabled>
					Needs a decision…
				</option>
				{STATUS_OPTIONS.map(o => (
					<option key={o.value} value={o.value}>
						{o.label}
					</option>
				))}
			</select>
			{action.busy && <span className="decision__busy">Saving…</span>}
		</div>
	);
};

/* ------------------------------------------------------------------- KYC */

const DOC_IMAGE_KEYS = ['frontImage', 'backImage', 'userPhoto', 'selfie'];

const DOC_LABELS: Record<string, string> = {
	frontImage: 'Document — Front',
	backImage: 'Document — Back',
	userPhoto: 'Liveness capture',
	selfie: 'Liveness capture',
};

/* The same three levels the legacy session header offers. Kept local rather
   than imported so the drawer does not pull in that screen's constants file
   (which also carries its table config); the values must stay in step with
   `AssignRiskLevelActions` in views/sessions/constants.tsx. */
const RISK_LEVELS = [
	{ label: 'High', value: 'high' },
	{ label: 'Medium', value: 'medium' },
	{ label: 'Low', value: 'low' },
];

/** A coloured inline chip for a risk level, used in the activity log. */
const riskChip = (value: string) => {
	const lvl = RISK_LEVELS.find(l => l.value === value);
	if (!lvl) return <b>{value}</b>;
	return <span className={`rpill rpill--${lvl.value}`}>{lvl.label}</span>;
};


/* ------------------------------------------------------------- biometrics */

/**
 * The applicant's location, drawn from `metadata.geoLocation`.
 *
 * Uses the same `@googlemaps/react-wrapper` + MapComponent the legacy session
 * page has always used, so the tile source, the marker and the API key are
 * identical — only the frame around it is sv2. The whole thing is a link to
 * Google Maps at the same coordinates, which is how the legacy page behaved.
 *
 * Renders nothing at all when the session carries no coordinates: an empty map
 * centred on 0,0 in the Gulf of Guinea is worse than no map.
 */
const SessionMap: FC<{ geo?: any }> = ({ geo }) => {
	const lat = Number(geo?.latitude);
	const lng = Number(geo?.longitude);
	if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
	if (!MAP_TOKEN) return null;

	return (
		<a
			className="smap"
			href={`${GOOGLE_API.MAP}${lat},${lng}`}
			target="_blank"
			rel="noreferrer"
			title="Open in Google Maps"
		>
			<Wrapper apiKey={MAP_TOKEN} render={() => <div className="smap__ph" />}>
				<MapComponent center={{ lat, lng }} zoom={11} />
			</Wrapper>
		</a>
	);
};

/**
 * Face-match confidence as a ring rather than a bar.
 *
 * The legacy page draws this as a full-width linear ProgressBar, which does not
 * survive the move into a drawer — it reads as a section separator at that
 * width. A ring carries the same single number in a fixed 84px box and sits
 * beside the media it describes.
 *
 * Drawn with two stroked circles and `stroke-dasharray`, so there is no charting
 * dependency and it inherits the theme through its tokens.
 */
const SimilarityDial: FC<{ value: number }> = ({ value }) => {
	/*
	 * DO NOT ROUND UP TO 100. Rekognition returned 99.62% and this printed
	 * "100%" — a claim of a perfect match that did not happen. On a figure a
	 * compliance reviewer signs off against, rounding in the flattering
	 * direction is the one thing it must never do.
	 *
	 * The value is shown to two decimals and trailing zeros are dropped, so a
	 * genuine 100 still reads "100%" while 99.62 reads "99.62%". The arc uses
	 * the same unrounded number.
	 */
	const raw = Math.max(0, Math.min(100, value));
	const pct = Number.isInteger(raw)
		? String(raw)
		: raw.toFixed(2).replace(/\.?0+$/, '');
	const R = 32;
	const C = 2 * Math.PI * R;
	/* PRODUCTION'S BANDS, not invented ones. handleAverageProgressColor in
	   kyc-reports/complex-kyc-report.tsx is `<33 red, 33-66 orange, else green`,
	   and a reviewer who has learned those numbers on the old page must not read
	   a different verdict off this dial for the same score. (Production's own
	   test is `33 < d && d < 66`, so exactly 33 falls through to green; that is a
	   boundary slip, not a rule, so 33 is amber here.) */
	const tone = raw >= 66 ? 'ok' : raw >= 33 ? 'rev' : 'rej';

	return (
		<div className={`sdial sdial--${tone}${raw === 0 ? ' sdial--zero' : ''}`}>
			<svg viewBox="0 0 80 80" role="img" aria-label={`Average confidence level ${pct}%`}>
				<circle className="sdial__track" cx="40" cy="40" r={R} />
				<circle
					className="sdial__arc"
					cx="40"
					cy="40"
					r={R}
					strokeDasharray={`${(C * raw) / 100} ${C}`}
				/>
				{/* "99.62%" does not fit at the same size as "97%", so the label
					scales to its own length rather than overflowing the ring. */}
				<text
					className="sdial__pct"
					x="40"
					y="44"
					style={{ fontSize: pct.length > 4 ? 11 : pct.length > 2 ? 13 : 15 }}
				>{`${pct}%`}</text>
			</svg>
			{/* NAME THE MEASUREMENT. "Avg. confidence" says nothing about what was
				compared, and this dial now sits directly beside the Liveness
				verdict — two percentages, inches apart, measuring entirely
				different things. This one is AWS Rekognition compareFaces: the
				captured selfie against the portrait on the government document.
				It answers "same person as the ID", not "a live person", and a
				printed photo of the applicant scores ~100% on it. */}
			<div className="sdial__cap">Face match to ID</div>
		</div>
	);
};

/**
 * Biometric Information — the liveness video, the captured selfie and the
 * face-match score, in the order the legacy page shows them.
 *
 * `faceVideoURL` is a SIGNED GCS url that the API recomputes on every read, so
 * it must be used from the payload as-is and never cached or rewritten.
 * `blobVideoUrl` is the older field and is still the fallback. Either may be an
 * empty string rather than absent, hence the trim.
 */
const BiometricSection: FC<{ payload: any; similarity?: number | null }> = ({
	payload,
	similarity,
}) => {
	const zoom = useContext(LightboxCtx);
	const [selfieBroken, setSelfieBroken] = useState(false);
	const [videoBroken, setVideoBroken] = useState(false);

	const bio = payload?.data?.data ?? payload?.data ?? payload ?? {};
	const selfie = typeof bio?.selfieImage === 'string' ? bio.selfieImage.trim() : '';
	const face = typeof bio?.faceVideoURL === 'string' ? bio.faceVideoURL.trim() : '';
	const blob = typeof bio?.blobVideoUrl === 'string' ? bio.blobVideoUrl.trim() : '';
	const palm = typeof bio?.plamVideoURL === 'string' ? bio.plamVideoURL.trim() : '';
	const video = face || blob;
	const live = bio?.facebiometric_information?.liveness_detected;
	/* The verdict pipelines writes beside the capture. `undefined` on any
	   session predating it — which reads as unadjudicated, correctly. */
	const verdict = bio?.verification?.verdict;
	const vconf = bio?.verification?.confidence;
	const vreason = bio?.verification?.reason;

	/* Show the dial whenever the KYC step actually produced a score. A genuine
	   0% is a finding — it means the face did not match the document — and
	   hiding it would read as "not checked", which is the opposite. `null` (the
	   field absent, or KYC never run) is what suppresses it. */
	const hasDial = typeof similarity === 'number';
	if (!selfie && !video && !palm && !hasDial) return null;

	return (
		<Section title="Biometric Information">
			<div className="bio">
				{!!video && !videoBroken && (
					<div className="bio__cell">
						{/* keyed on the url: a signed url that refreshes must remount the
							element, or the browser keeps playing the stale source */}
						<video
							key={video}
							className="bio__media"
							controls
							muted
							loop
							playsInline
							onError={() => setVideoBroken(true)}
						>
							<source src={video} type="video/mp4" />
							<source src={video} type="video/webm" />
						</video>
						{/* ENLARGE, ON THE VIDEO, TOP RIGHT.
							A separate control rather than a click on the <video> itself:
							the element's own surface belongs to its play/scrub controls,
							and hijacking a click there would fight them. Top-right for
							the same reason — the native controls occupy the bottom edge,
							so anything placed there would sit on the scrubber.

							The recording is 176px wide in the drawer, which is enough to
							see that a video exists and not enough to judge a face. This
							is the affordance that says so: a caption reading "Liveness
							video ⤢" underneath was doing the job, but nothing about it
							looked like a button. */}
						<button
							type="button"
							className="bio__zoom"
							title="Enlarge liveness video"
							aria-label="Enlarge liveness video"
							onClick={() => zoom?.(video, 'Liveness video', 'video')}
						>
							<span aria-hidden="true">⤢</span>
						</button>
						<div className="bio__cap">Liveness video</div>
					</div>
				)}

				{!!selfie && !selfieBroken && (
					<div className="bio__cell">
						<button
							type="button"
							className="bio__shot"
							title="Click to enlarge"
							onClick={() => zoom?.(selfie, 'Liveness selfie')}
						>
							<img
								className="bio__media"
								src={selfie}
								alt="Liveness selfie"
								onError={() => setSelfieBroken(true)}
							/>
						</button>
						<div className="bio__cap">Selfie</div>
					</div>
				)}

				{!!palm && (
					<div className="bio__cell">
						<video key={palm} className="bio__media" controls muted loop playsInline>
							<source src={palm} type="video/mp4" />
							<source src={palm} type="video/webm" />
						</video>
						<div className="bio__cap">Palm print</div>
					</div>
				)}

				{hasDial && <SimilarityDial value={similarity as number} />}
			</div>

			{/*
			 * SAY WHEN NOTHING JUDGED IT, rather than showing no row at all.
			 *
			 * This rendered only when `liveness_detected` was a boolean, so a
			 * capture nobody adjudicated produced NO Liveness row — and an
			 * absent row reads as "not applicable", not as "unverified". The
			 * step's own chip was green at the same time, so the page as a whole
			 * said liveness had passed.
			 *
			 * `verification.verdict` is what pipelines now writes beside the
			 * capture; `unverified` is the honest state while no adjudicator is
			 * wired. Note `liveness_detected` itself is not evidence either —
			 * upstream's webrtc.api hardcodes it to True whenever the stream
			 * stops (satschel/webrtc.api, src/webrtc/video.py) — so it is shown
			 * as the provider's claim, and the verdict is shown separately.
			 */}
			<KV
				rows={[
					[
						/* The label says what was captured; the value says whether
						   anything judged it.
						   "Liveness & Similarity" because the percentage beside the
						   verdict is a FACE-MATCH confidence, not a liveness score —
						   labelling the row "Liveness" alone invited reading 88% as
						   "88% sure this was a live person", which is not what the
						   number means. */
						'Liveness & Similarity',
						verdict === 'passed' ? (
							<span className="okv">
								Passed{typeof vconf === 'number' ? ` · ${(vconf * 100).toFixed(0)}%` : ''}
							</span>
						) : verdict === 'failed' ? (
							<span className="badv">Failed</span>
						) : verdict === 'review' ? (
							<span className="warnv">
								Needs review
								{typeof vconf === 'number' ? ` · ${(vconf * 100).toFixed(0)}%` : ''}
							</span>
						) : (
							<span className="warnv">Not adjudicated</span>
						),
					],
					...(vreason && verdict !== 'passed'
						? ([['Why', <span key="liveness-why" className="muted">{vreason}</span>]] as any)
						: []),
					...(typeof live === 'boolean'
						? ([
								[
									'Provider claim',
									<span key="liveness-provider-claim" className={live ? 'okv' : 'badv'}>
										{live ? 'Detected' : 'Not detected'}
									</span>,
								],
						  ] as any)
						: []),
				]}
			/>
		</Section>
	);
};

/**
 * __simplici_criminal_background_check__: the $5 offender/criminal records
 * search, rendered from the node's own payload.
 *
 * It is a SIBLING of `kyc` on the node (like `ssn`), not a field inside it, so
 * KycSection's sweep never reaches it — a section of its own is also the honest
 * presentation: this is a different check, from a different vendor, that says
 * nothing about whether the identity verified.
 *
 * THE FULL VENDOR RESPONSE IS SHOWN, not just a verdict. A criminal record
 * search is evidence someone acts on, and the summary is derived from a shape
 * this code does not control — so the payload it was derived from is always one
 * click away, verbatim, rather than being trusted to have been summarised
 * correctly.
 */
/**
 * Join name parts without repeating one. Providers disagree about whether
 * `first_name` already contains the middle name — Didit's does — so a naive
 * join renders "Austin Dale Dale Trombley". Compared on whole words, case- and
 * punctuation-insensitively, so "Dale" is dropped but "Ann" would survive
 * inside "Annabel".
 */
const joinName = (parts: (string | null | undefined)[]): string => {
	const out: string[] = [];
	const seen = new Set<string>();
	for (const part of parts) {
		for (const word of String(part ?? '').trim().split(/\s+/)) {
			const key = word.replace(/[^\p{L}\p{N}]/gu, '').toLowerCase();
			if (!key || seen.has(key)) continue;
			seen.add(key);
			out.push(word);
		}
	}
	return out.join(' ');
};

/* ---------------------------------------- criminal background check helpers */

/** `matchBasis` is a space-separated STRING on the criminal index and an ARRAY
 *  on the sex-offender one. Both are read. */
const basisArr = (r: any): string[] =>
	Array.isArray(r?.matchBasis)
		? r.matchBasis
		: String(r?.matchBasis ?? '')
				.split(/\s+/)
				.filter(Boolean);

/**
 * WHAT ANCHORS THIS MATCH TO A PERSON.
 *
 * The criminal index rarely publishes a birth date: a 0.98 is typically name +
 * age-within-tolerance. Treating that as a confirmed identity is the single
 * most consequential misreading this panel can invite, so the anchor is derived
 * once and drives the tag, the strip and the basis chips together.
 */
const dobKind = (r: any): 'ok' | 'age' | 'none' => {
	const b = basisArr(r);
	if (b.some(x => /^dob:(exact|day|full)/i.test(x))) return 'ok';
	if (b.some(x => /^dob:.*age/i.test(x)) || r?.dob?.dobPrecision === 'age' || r?.dobPrecision === 'age')
		return 'age';
	return 'none';
};

/** Confidence banded on TWO axes: how sure, and of what. Without a confirmed
 *  date of birth the noun stays "NAME MATCH" however high the number. */
const confBand = (c: number, kind: string) => {
	const noun = kind === 'ok' ? 'MATCH' : 'NAME MATCH';
	return c >= 0.85
		? { tag: `STRONG ${noun}`, cls: 'strong', rec: '' }
		: c >= 0.65
		? { tag: `POSSIBLE ${noun}`, cls: 'mid', rec: ' cbc-rec--mid' }
		: { tag: `WEAK ${noun}`, cls: 'weak', rec: ' cbc-rec--weak' };
};

/** `{first, middle, last}` or a bare string. */
const recName = (r: any): string =>
	typeof r?.name === 'string'
		? r.name
		: [r?.name?.first, r?.name?.middle, r?.name?.last, r?.name?.suffix]
				.filter(Boolean)
				.join(' ')
				.trim() || (r?.matchedName ?? '');

/** The payload mixes ISO datetimes with "05/05/1981" and "08/17/2026 02:56 PM". */
const fmtD = (d: any): string | null => {
	const t = String(d ?? '').trim();
	if (!t) return null;
	const iso = t.match(/^\d{4}-\d{2}-\d{2}/);
	return iso?.[0] ?? t;
};

/** The date of birth as the index actually holds it — precision included, never
 *  implied. "age 45 — no birth date published" is a different claim from a date. */
const recDob = (r: any): string | null => {
	const d = r?.dob;
	if (!d) return r?.birthYear ? `${r.birthYear} (year only)` : null;
	if (typeof d === 'string') return d;
	if (d.date) return String(d.date);
	if (d.birthYear) return `${d.birthYear} (year only)`;
	if (d.age != null) return `age ${d.age} — no birth date published`;
	return null;
};

const TYPE_LABEL: { [k: string]: string } = {
	doc_inmate: 'DOC inmate record',
	court_case: 'Court case',
	jail_booking: 'Jail booking',
	warrant: 'Warrant',
};
const TYPE_SHORT: { [k: string]: string } = {
	doc_inmate: 'DOC',
	court_case: 'court',
	jail_booking: 'jail',
	warrant: 'warrant',
};

/**
 * __simplici_criminal_background_check__: the $5 criminal records search.
 *
 * A SIBLING of `kyc` on the node (like `ssn`), not a field inside it, so
 * KycSection's sweep never reaches it — and a section of its own is the honest
 * presentation: a different check, from a different source, saying nothing
 * about whether the identity verified.
 *
 * THE PROVIDER IS NEVER NAMED. Simplici runs the check; who it is bought from
 * is not the reviewer's business and not the customer's.
 *
 * THE FULL RESPONSE IS ALWAYS REACHABLE. A summary is derived from a shape this
 * code does not control; the payload it came from stays one click away.
 */
const CriminalCheckSection: FC<{ payload: any }> = ({ payload }) => {
	const check =
		payload?.data?.criminalBackgroundCheck ?? payload?.criminalBackgroundCheck ?? null;
	const [showRaw, setShowRaw] = useState(false);
	const [openWeak, setOpenWeak] = useState<Set<number>>(new Set());
	if (!check) return null;

	const {
		status,
		recordsFound,
		recordCount,
		partial,
		partialReason,
		reason,
		subject,
		billed,
		legalNotice,
		raw,
	} = check;
	const counts: any = (raw as any)?.counts ?? {};
	const records: any[] = Array.isArray((raw as any)?.records) ? (raw as any).records : [];

	const REASONS: { [k: string]: string } = {
		not_configured: 'The provider is not configured on this environment.',
		insufficient_identity:
			'The KYC document did not yield enough identity to search on — a name and a date of birth are both required.',
		provider_error: 'The provider did not answer.',
	};

	/* Strongest first, custody breaking ties: the thing a reviewer must not miss
	   should never be below the fold. */
	const sorted = [...records].sort(
		(a, b) =>
			(Number(b.matchConfidence) || 0) - (Number(a.matchConfidence) || 0) ||
			Number(!!b.currentlyIncarcerated) - Number(!!a.currentlyIncarcerated)
	);
	const strong = sorted.filter(r => (Number(r.matchConfidence) || 0) >= 0.85);
	const weak = sorted.filter(r => (Number(r.matchConfidence) || 0) < 0.85);

	const inCustody = records.filter(r => r.currentlyIncarcerated).length;
	const warrants = records.filter(
		r => r.currentStatus === 'absconder' || r.recordType === 'warrant'
	).length;
	const anyConfirmedDob = records.some(r => dobKind(r) === 'ok');

	/* ---- the claim, fused with its scope ------------------------------------ */
	let band: { v: string; t: any; d: any; n?: any };
	if (status !== 'completed') {
		band = {
			v: 'dead',
			t: status === 'failed' ? 'Search failed' : 'Search not run',
			d: REASONS[reason as string] ?? 'The search did not run.',
			n: 'This is not a clear result — nothing was searched, and nothing was charged.',
		};
	} else if (recordsFound) {
		const custody = [
			inCustody ? `${inCustody} currently in custody` : '',
			warrants ? `${warrants} outstanding warrant${warrants === 1 ? '' : 's'}` : '',
		]
			.filter(Boolean)
			.join(', ');
		band = {
			v: 'hit',
			t: (
				<>
					<span className="cnt">{recordCount}</span> possible record
					{recordCount === 1 ? '' : 's'} matched
				</>
			),
			d: (
				<>
					Fuzzy name matches from the consolidated criminal index — not convictions
					attributed to this person.
					{custody ? ` ${custody}.` : ''}
				</>
			),
			n: (
				<>
					{anyConfirmedDob
						? 'Each match below says how it matched and how confident the index is.'
						: 'None is a confirmed identity: the index published no birth date to check against the document — the strongest are name-and-age matches.'}
					{(counts.records ?? 0) > (counts.recordsReturned ?? 0) && (
						<span className="a">
							{' '}
							The response was capped, so this is not the whole list.
						</span>
					)}
				</>
			),
		};
	} else if (partial) {
		band = {
			v: 'open',
			t: 'No records — in an incomplete search',
			d: `The search did not complete (${partialReason}).`,
			n: 'Not a clean clear — the part that did not run could hold a record.',
		};
	} else {
		band = {
			v: 'clear',
			t: 'No records found',
			d: (
				<>
					Nothing matched in the consolidated criminal index, and nothing was withheld —{' '}
					<b>
						{counts.records ?? 0} matched, {counts.recordsReturned ?? 0} returned,
						uncapped
					</b>
					.
				</>
			),
			/* SAY WHAT THE CLEAR DOES NOT COVER. The index does not report
			   per-jurisdiction coverage, so "no match" is not "every county was
			   searched" — and the difference is exactly what someone acting on a
			   clear result would otherwise assume. */
			n: 'The index consolidates DOC inmate rosters, county jail bookings, court cases and warrants. It does not report per-jurisdiction coverage, so this is “no match in the index” — not a certificate that every county was searched.',
		};
	}

	const chip =
		status === 'skipped'
			? { v: 'ns' as const, t: 'NOT RUN' }
			: status === 'failed'
			? { v: 'rev' as const, t: 'FAILED' }
			: recordsFound
			? { v: 'rej' as const, t: 'RECORDS' }
			: partial
			? { v: 'rev' as const, t: 'PARTIAL' }
			: { v: 'ok' as const, t: 'CLEAR' };

	const meta =
		status === 'completed'
			? `criminal index · ${recordCount} record${recordCount === 1 ? '' : 's'}`
			: 'criminal index';

	const subjName = joinName([subject?.firstName, subject?.middleName, subject?.lastName]);
	const subjAddr = [subject?.address, subject?.city, subject?.state, subject?.postalCode]
		.filter(Boolean)
		.join(', ');

	/* Where the returned matches sit — grouped from the records themselves.
	   Deliberately NOT a coverage map: the index reports no per-jurisdiction
	   completeness, and a map implying it did would be the grid's old lie. */
	const typeLn = Object.entries(
		records.reduce((acc: any, r: any) => {
			const k = TYPE_SHORT[r.recordType] ?? r.recordType ?? 'other';
			acc[k] = (acc[k] ?? 0) + 1;
			return acc;
		}, {})
	)
		.map(([k, n]) => `${n} ${k}`)
		.join(' · ');
	const stateCounts = Object.entries(
		records.reduce((acc: any, r: any) => {
			const k = r.state === 'US' ? 'FED' : r.state || '—';
			acc[k] = (acc[k] ?? 0) + 1;
			return acc;
		}, {})
	).sort((a: any, b: any) => b[1] - a[1]);

	const fullCard = (r: any, i: number) => {
		const conf = Number(r.matchConfidence) || 0;
		const kind = dobKind(r);
		const b = confBand(conf, kind);
		const basis = basisArr(r);
		const county = r.county ? `${r.county} Co` : '';
		const stateLbl = r.state === 'US' ? 'Federal (BOP)' : r.state;
		return (
			<div className={`cbc-rec${b.rec}`} key={r.externalId ?? r.recordId ?? i}>
				<div className="cbc-rec__hd">
					<span className="cbc-rec__name">{recName(r) || `Record ${i + 1}`}</span>
					<span className="cbc-conf">
						<span className="cbc-conf__bar">
							<i style={{ width: `${Math.round(conf * 100)}%` }} />
						</span>
						<span className="cbc-conf__n">{conf.toFixed(2)}</span>
					</span>
					<span className={`cbc-rec__tag cbc-rec__tag--${b.cls}`}>{b.tag}</span>
				</div>

				<div className={`cbc-anchor cbc-anchor--${kind}`}>
					<b>
						{kind === 'ok'
							? 'DOB confirmed'
							: kind === 'age'
							? 'Age match only'
							: 'No DOB to check'}
					</b>
					{typeof r.matchDetail === 'string' ? r.matchDetail : ''}
					{kind === 'age' &&
						' — the confidence is a name-and-age claim, not a confirmed identity.'}
				</div>

				{r.currentStatus === 'absconder' ? (
					<div className="cbc-cust cbc-cust--warrant">
						<b>WARRANT</b>
						<span>
							parole absconder{county || r.state ? ` — ${[county, r.state].filter(Boolean).join(', ')}` : ''}
						</span>
					</div>
				) : r.currentlyIncarcerated ? (
					<div className="cbc-cust cbc-cust--now">
						<b>IN CUSTODY</b>
						<span>
							{[
								r.custodyFacility,
								stateLbl ? `(${stateLbl})` : '',
								fmtD(r.dateIncarcerated) ? `since ${fmtD(r.dateIncarcerated)}` : '',
								fmtD(r.releaseDate) ? `release ${fmtD(r.releaseDate)}` : '',
							]
								.filter(Boolean)
								.join(' · ')}
						</span>
					</div>
				) : r.currentStatus === 'released' || r.everIncarcerated ? (
					<div className="cbc-cust cbc-cust--past">
						<b>RELEASED</b>
						<span>
							{fmtD(r.actualReleaseDate ?? r.releaseDate) ??
								'previously incarcerated — no date published'}
						</span>
					</div>
				) : null}

				<div className="cbc-basis">
					{basis.map(x => {
						const isDob = /^dob:/i.test(x);
						const exact = /:exact$/.test(x);
						const softDob = isDob && !/^dob:(exact|day|full)/i.test(x);
						return (
							<span
								key={x}
								className={`cbc-b${
									softDob
										? ' cbc-b--warn'
										: isDob
										? ' cbc-b--dob'
										: exact
										? ' cbc-b--hard'
										: ''
								}`}
							>
								{x
									.replace(/^dob:ageFromDob$/i, 'dob · age only')
									.replace(/^dob:absent$/i, 'dob · absent')
									.replace(/_/g, ' ')
									.replace(/:exact$/, ' · exact')}
							</span>
						);
					})}
					{r.unverified && <span className="cbc-b cbc-b--warn">unverified</span>}
				</div>

				<KV
					rows={[
						[
							'Source',
							[TYPE_LABEL[r.recordType] ?? r.recordType, stateLbl, county]
								.filter(Boolean)
								.join(' · ') || null,
						],
						['Date of birth', recDob(r)],
						['Description', [r.sex, r.race].filter(Boolean).join(' · ') || null],
						[
							'Sentence',
							r.sentence
								? [r.sentence.length, fmtD(r.sentence.maxDate) && `max ${fmtD(r.sentence.maxDate)}`]
										.filter(Boolean)
										.join(' · ')
								: null,
						],
						[
							'Parole',
							r.parole
								? [
										fmtD(r.parole.eligibilityDate) && `eligible ${fmtD(r.parole.eligibilityDate)}`,
										fmtD(r.parole.nextReviewDate) && `review ${fmtD(r.parole.nextReviewDate)}`,
										r.parole.lastDecision && r.parole.lastDecision !== 'None'
											? r.parole.lastDecision
											: '',
								  ]
										.filter(Boolean)
										.join(' · ')
								: null,
						],
						[
							'Prior felony',
							r.priorFelony == null ? null : (
								<span className={r.priorFelony ? 'rd' : 'g'}>
									{r.priorFelony ? 'yes' : 'no'}
								</span>
							),
						],
						['Aliases', (r.aliases ?? []).join(', ') || null],
					]}
				/>

				{(r.cases ?? []).map((cs: any, ci: number) => (
					// eslint-disable-next-line react/no-array-index-key
					<div className="cbc-case" key={ci}>
						<div className="cbc-case__hd">
							{[cs.caseNumber, cs.court, cs.county && `${cs.county} Co`, cs.state, cs.status]
								.filter(Boolean)
								.join(' · ')}
						</div>
						{(cs.charges ?? []).map((ch: any, hi: number) => (
							// eslint-disable-next-line react/no-array-index-key
							<div className="cbc-chg" key={hi}>
								<span className="cbc-chg__d">{ch.description || ch.statute}</span>
								<i className={`sev sev--${ch.severity ?? 'none'}`}>
									{ch.severity ?? 'unclassified'}
								</i>
								<br />
								<span className="cbc-chg__m">
									{[
										ch.disposition,
										fmtD(ch.dispositionDate),
										fmtD(ch.offenseDate) && `offense ${fmtD(ch.offenseDate)}`,
										ch.plea && ch.plea !== ch.disposition ? `plea ${ch.plea}` : '',
									]
										.filter(Boolean)
										.join(' · ')}
								</span>
							</div>
						))}
					</div>
				))}

				<div className="cbc-rec__ft">
					{r.externalId}
					{fmtD(r.lifecycle?.lastSeen) ? ` · index sweep ${fmtD(r.lifecycle.lastSeen)}` : ''}
					{' · '}
					{r.liveChecked ? (
						'checked live against the source'
					) : (
						<span className="a">not checked live against the source</span>
					)}
					{r.lifecycle?.droppedOut && (
						<span className="a"> · dropped out of the source since</span>
					)}
				</div>
			</div>
		);
	};

	return (
		<Section
			title="Criminal Background Check"
			right={
				<>
					<StatusChip verdict={chip.v}>{chip.t}</StatusChip>
					<span className="cbc-meta">{meta}</span>
				</>
			}
		>
			<div className={`cbc-band cbc-band--${band.v}`}>
				<span className="cbc-band__t">{band.t}</span>
				<span className="cbc-band__d">{band.d}</span>
				{band.n && <span className="cbc-band__n">{band.n}</span>}
			</div>

			{/* A standing condition on the whole panel, not an alert — deliberately
				outside the verdict palette so it never trains a reviewer to skim
				amber. */}
			{legalNotice && (
				<div className="cbc-fcra">
					<b>Not a consumer report</b>
					{legalNotice}
				</div>
			)}

			{records.length > 0 && (
				<>
					<div className="grp">Where the matches sit</div>
					<div className="cbc-mix">
						<div className="cbc-mix__ln">
							<b>{records.length}</b> returned records · {typeLn} — grouped from the
							records themselves; where the matches live, <b>not</b> a coverage map.
						</div>
						<div className="cbc-states">
							{stateCounts.map(([st, n]: any) => (
								<span className="cbc-st" key={st}>
									{st} {n}
								</span>
							))}
						</div>
					</div>
				</>
			)}

			<div className="grp">Identity searched</div>
			<KV
				rows={[
					['Name', subjName ? <Verified>{subjName}</Verified> : null],
					['Date of birth', subject?.dateOfBirth ?? null],
					['Address', subjAddr || null],
				]}
			/>
			<div className="cbc-src">
				Taken from the verified ID document, not from anything the applicant typed.
			</div>

			{strong.length > 0 && <div className="grp">Record matches — strongest first</div>}
			{strong.map((r, i) => fullCard(r, i))}

			{weak.length > 0 && (
				<div className="grp">
					Name matches below 0.85 — {weak.length} · click to unfold
				</div>
			)}
			{weak.map((r, i) => {
				const conf = Number(r.matchConfidence) || 0;
				const open = openWeak.has(i);
				const custFlag = r.currentlyIncarcerated
					? 'in custody'
					: r.currentStatus === 'absconder'
					? 'warrant'
					: '';
				return (
					<Fragment key={r.externalId ?? i}>
						<div
							className={`cbc-row${open ? ' cbc-row--x' : ''}`}
							role="button"
							tabIndex={0}
							onClick={() =>
								setOpenWeak(prev => {
									const next = new Set(prev);
									// eslint-disable-next-line @typescript-eslint/no-unused-expressions
									next.has(i) ? next.delete(i) : next.add(i);
									return next;
								})
							}
							onKeyDown={e => {
								if (e.key === 'Enter' || e.key === ' ')
									setOpenWeak(prev => {
										const next = new Set(prev);
										// eslint-disable-next-line @typescript-eslint/no-unused-expressions
										next.has(i) ? next.delete(i) : next.add(i);
										return next;
									});
							}}
						>
							<span className="cbc-row__ar">{open ? '▼' : '▶'}</span>
							<span className="cbc-row__n">{recName(r)}</span>
							<span className="cbc-row__t">
								{TYPE_SHORT[r.recordType] ?? r.recordType} ·{' '}
								{r.state === 'US' ? 'FED' : r.state}
							</span>
							{custFlag && <span className="cbc-row__k">{custFlag}</span>}
							<span className="cbc-row__c">{conf.toFixed(2)}</span>
						</div>
						{open && fullCard(r, i)}
					</Fragment>
				);
			})}

			{/* THE COUNT IS THE FINDING, NOT THE LIST — counted off the envelope, not
				off the array, because the array is what survived the cap. */}
			{(counts.records ?? 0) > (counts.recordsReturned ?? 0) && (
				<div className="cbc-more">
					The index matched <b>{counts.records}</b> records but returned{' '}
					{counts.recordsReturned} (capped at {(raw as any)?.cappedLimit}
					{(raw as any)?.cappedReason ? `: ${(raw as any).cappedReason}` : ''}). The count
					is the finding; the rest are reachable by paging the provider response.
				</div>
			)}

			{/* THE PROVIDER IS NEVER NAMED. The search id stays — it is how support
				traces one result back to one call, and it carries no brand. */}
			<div className="cbc-foot">
				{(raw as any)?.searchId ? `Search ${(raw as any).searchId}` : 'Criminal records search'}
				{billed?.price
					? ` · billed $${Number(billed.price).toFixed(2)}`
					: status === 'completed'
					? ''
					: ' · not billed'}
				{(raw as any)?.meterEvents?.[0]
					? ` · ${(raw as any).meterEvents[0].quantity} ${(raw as any).meterEvents[0].kind}`
					: ''}
			</div>

			{raw !== null && raw !== undefined && (
				<>
					<button
						type="button"
						className="cbc-raw-toggle"
						onClick={() => setShowRaw(v => !v)}
					>
						{showRaw ? 'Hide' : 'Show'} full provider response
					</button>
					{showRaw && <pre className="cbc-raw">{JSON.stringify(raw, null, 2)}</pre>}
				</>
			)}
		</Section>
	);
};

const KycSection: FC<{ payload: any; action?: IStepAction }> = ({ payload, action }) => {
	const zoom = useContext(LightboxCtx);
	const kyc = payload?.data?.kyc ?? payload?.kyc ?? null;
	if (!kyc) return null;

	const images: { key: string; url: string; verified: boolean }[] = [];
	Object.values(kyc).forEach((section: any) => {
		DOC_IMAGE_KEYS.forEach(k => {
			const url = val(section?.[k]);
			if (url && /^https?:/.test(url))
				images.push({ key: k, url, verified: isVerified(section[k]) });
		});
	});

	const conclusion = val(kyc?.breakdown?.conclusion) ?? val(kyc?.conclusion);

	/*
	 * The SSN row. It is a SIBLING of `kyc` on the node payload, not a field
	 * inside it, so the section sweep below never reaches it — which is why a
	 * flow with SSN verification switched on collected the number, sent it to
	 * Didit, stored the last four and then showed the reviewer nothing.
	 *
	 * Only the last four exist server-side; the number itself is never kept.
	 * pipelines.api hands it over already masked, so there is no digit here to
	 * leak even if this rendered it raw.
	 */
	const ssnField = payload?.data?.ssn ?? payload?.ssn ?? null;
	const ssnValue = val(ssnField);

	return (
		<Section
			title="KYC Details"
			right={conclusion ? `Conclusion : ${conclusion}` : undefined}
		>
			<StepStatus action={action} />
			{!!ssnValue && (
				<KV
					rows={[
						[
							'SSN',
							isVerified(ssnField) ? (
								<Verified>{ssnValue}</Verified>
							) : (
								ssnValue
							),
						],
					]}
				/>
			)}
			{images.length > 0 && (
				<div className="docs">
					{images.map(img => (
						<div
							className="docbox docbox--zoom"
							key={img.key}
							role="button"
							tabIndex={0}
							title="Click to enlarge"
							onClick={() => zoom?.(img.url, DOC_LABELS[img.key] ?? img.key)}
							onKeyDown={e => {
								if (e.key === 'Enter' || e.key === ' ')
									zoom?.(img.url, DOC_LABELS[img.key] ?? img.key);
							}}
						>
							<img src={img.url} alt={DOC_LABELS[img.key] ?? img.key} />
							{DOC_LABELS[img.key] ?? pretty(img.key)}
							{img.verified && (
								<>
									<br />
									<span className="vtag">VERIFIED</span>
								</>
							)}
						</div>
					))}
				</div>
			)}

			{Object.entries(kyc).map(([sectionKey, section]: [string, any]) => {
				if (!section || typeof section !== 'object') return null;
				const rows = Object.entries(section)
					.filter(([k]) => !DOC_IMAGE_KEYS.includes(k))
					.map(([k, field]: [string, any]) => {
						const text = val(field);
						if (!text) return null;
						return [
							pretty(k),
							isVerified(field) ? <Verified>{text}</Verified> : text,
						] as [string, any];
					})
					.filter(Boolean) as [string, any][];
				if (!rows.length) return null;
				return (
					<div key={sectionKey}>
						<div className="grp">{pretty(sectionKey)}</div>
						<KV rows={rows} />
					</div>
				);
			})}
		</Section>
	);
};

/* --------------------------------------------------------- accreditation */

/* ------------------------------------------------------- document actions */

/**
 * Open a document in a new tab, by clicking an anchor rather than by calling
 * `window.open`.
 *
 * THIS DISTINCTION IS THE WHOLE BUG BELOW, so it is worth stating plainly.
 * `window.open(url, '_blank', 'noopener')` passes a windowFeatures string, and
 * a call carrying windowFeatures is a POPUP as far as Chrome is concerned.
 * Popups are allowed only while the click's transient user activation is still
 * live — which it is NOT once the handler has `await`ed something. The call
 * then returns `null` and nothing at all happens: no file, no tab, no error.
 * An anchor click is an ordinary user navigation, not a popup, and is not
 * blocked; `rel="noreferrer"` keeps the property `noopener` was there for
 * (noreferrer implies noopener).
 */
const openDocumentInTab = (url: string) => {
	const a = document.createElement('a');
	a.href = url;
	a.target = '_blank';
	a.rel = 'noreferrer';
	document.body.appendChild(a);
	a.click();
	a.remove();
};

/**
 * Save a document the reviewer is looking at.
 *
 * `<a download>` is ignored cross-origin — the signed URLs here are served
 * from storage, so the attribute silently degrades to a navigation and the
 * reviewer gets the PDF in a tab instead of on disk. Fetching it as a blob
 * first makes the download real. If the fetch is refused (CORS, an expired
 * signature) we open it rather than failing silently: a reviewer who asked for
 * the document should end up looking at it either way.
 *
 * THE FALLBACK IS NOT A LUXURY — IT IS THE ONLY PATH FOR A WHOLE CLASS OF
 * DOCUMENT. An accreditation record built from an uploaded file carries a raw
 * `https://storage.googleapis.com/satschel-accreditation/...` signed URL
 * (accreditation.api's `link-details` hands one straight through), and that
 * bucket sends no `Access-Control-Allow-Origin`, so the fetch above can never
 * succeed from the panel. Every one of those downloads therefore lands here.
 * With the old `window.open(url, '_blank', 'noopener')` the popup blocker ate
 * it and the Download button did nothing whatsoever — the reported
 * "Download Accreditation pdf doesn't work". Documents served by our own API
 * (`/v2/esign/pdf/fetch/...`, which does send CORS headers) were unaffected,
 * which is why the same button worked on an e-signed attestation and was dead
 * on an uploaded one.
 */
const saveDocument = async (url: string, filename: string) => {
	try {
		const resp = await fetch(url);
		if (!resp.ok) throw new Error(String(resp.status));
		const blob = await resp.blob();
		const href = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = href;
		a.download = filename;
		document.body.appendChild(a);
		a.click();
		a.remove();
		// revoked on the next tick so Safari has actually started the download
		setTimeout(() => URL.revokeObjectURL(href), 4000);
	} catch {
		openDocumentInTab(url);
	}
};

/** Open / Download for one document. Both actions, always, side by side. */
/**
 * `downloadUrl`, when the backend supplies one, is a SAME-ORIGIN route that
 * streams the same document as an attachment.
 *
 * It exists because an uploaded accreditation document lives in a GCS bucket
 * that sends no Access-Control-Allow-Origin: the browser will not let this page
 * read those bytes, and `<a download>` is ignored cross-origin, so Download
 * could only ever open a tab. Given a same-origin URL both work properly.
 *
 * Open deliberately keeps using the direct `url` — it needs no CORS, and going
 * straight to storage saves a hop through our API for the common case of a
 * reviewer just looking at the document.
 */
const DocActions: FC<{
	url: string;
	filename: string;
	label: string;
	downloadUrl?: string;
}> = ({ url, filename, label, downloadUrl }) => (
	<div className="docact">
		<span className="docact__name">{label}</span>
		<a className="docact__btn" href={url} target="_blank" rel="noreferrer">
			Open
		</a>
		<button
			type="button"
			className="docact__btn"
			onClick={() => saveDocument(downloadUrl || url, filename)}
		>
			Download
		</button>
	</div>
);

/**
 * The signed agreement. The drawer had NO Sign Agreement section at all, so a
 * flow whose whole purpose is an executed document showed the reviewer nothing
 * of it — not the status, not the envelope, and not the document itself.
 */
const SignAgreementSection: FC<{ payload: any; action?: IStepAction }> = ({
	payload,
	action,
}) => {
	const list = Array.isArray(payload?.data) ? payload.data : [];
	const entry = list[0] ?? null;
	if (!entry) return null;

	const url = val(entry.url);
	const csv = val(entry.csvUrl);
	const envStatus = val(entry.envelopeStatus);
	const envId = val(entry.envelopeId);
	const parties = (Array.isArray(entry.recipients) ? entry.recipients : [])
		.slice()
		.sort((a: any, b: any) => Number(a?.order ?? 0) - Number(b?.order ?? 0));

	return (
		<Section
			title="Sign Agreement"
			right={envStatus ? `Envelope : ${envStatus}` : undefined}
		>
			<StepStatus action={action} />
			<KV
				rows={[
					['Envelope status', envStatus],
					['Envelope ID', envId],
				]}
			/>

			{/* WHO SIGNED IT. An agreement can carry several parties — the
				applicant, a counter-signer, a third-party attesting to an
				accreditation — and "executed" means nothing without knowing by
				whom. Ordered by signing order where the envelope defines one. */}
			{!!parties.length && (
				<>
					<div className="grp">
						{parties.length > 1 ? 'Parties' : 'Party'}
					</div>
					{parties.map((r: any, i: number) => (
						<KV
							// eslint-disable-next-line react/no-array-index-key
							key={`${r.email || r.name || 'party'}-${i}`}
							rows={[
								['Name', val(r.name)],
								['Title', val(r.title)],
								['Email', val(r.email)],
								['Role', pretty(val(r.role) ?? val(r.type) ?? '')],
								[
									'Signed',
									r.status ? (
										<span className={r.status === 'completed' ? 'g' : 'a'}>
											{r.status === 'completed' && r.signedAt
												? dayTime(r.signedAt)
												: r.status}
										</span>
									) : null,
								],
							]}
						/>
					))}
				</>
			)}
			{!!url && (
				<DocActions
					url={url}
					filename={`agreement-${envId || 'signed'}.pdf`}
					label="Signed agreement"
				/>
			)}
			{!!csv && (
				<DocActions
					url={csv}
					filename={`agreement-${envId || 'signed'}-fields.csv`}
					label="Completed field data (CSV)"
				/>
			)}
			{!url && (
				<div className="foot">
					The envelope exists but no signed document has been produced yet.
				</div>
			)}
		</Section>
	);
};

/**
 * The applicant's questionnaire answers.
 *
 * The drawer rendered nothing for this step, so a flow whose whole purpose is
 * asking the investor questions showed the reviewer none of the answers.
 *
 * Each entry carries `title` (the question as asked) and both `value` and
 * `displayValue` — the stored code and the human label for a choice. The label
 * is shown, falling back to the raw value, because "Individual" is what the
 * applicant saw and "individual" is only how it is stored.
 */
const DynamicFormSection: FC<{ payload: any; action?: IStepAction; title: string }> = ({
	payload,
	action,
	title,
}) => {
	const survey =
		payload?.data?.data?.survey ?? payload?.data?.survey ?? payload?.survey ?? [];
	if (!Array.isArray(survey) || !survey.length) return null;

	const rows = survey
		.map((q: any) => {
			const label = val(q?.title) ?? val(q?.name);
			const answer = val(q?.displayValue) ?? val(q?.value);
			return label ? ([label, answer] as [string, any]) : null;
		})
		.filter(Boolean) as [string, any][];

	if (!rows.length) return null;

	return (
		<Section title={title} right={`${rows.length} answer(s)`}>
			<StepStatus action={action} />
			<KV rows={rows} />
		</Section>
	);
};


const ACCREDITATION_FIELD_LABEL: Record<string, string> = {
	// the old panel's renderKey(), kept verbatim so a reviewer sees the names
	// they already know
	createdAt: 'Created Date',
	updatedAt: 'Updated Date',
	_id: 'Id',
	id: 'Id',
	kybStatus: 'KYB Status',
	envelopeStatus: 'Packet status',
	csvUrl: 'CSV Url',
	envelopeId: 'Envelope Id',
	crdNumber: 'CRD number',
	license: 'License',
	source: 'Source',
	type: 'Document type',
	provider: 'Provider',
	name: 'Document name',
	fileName: 'File name',
};

/* Keys rendered by hand above the walk, or never meant for a reviewer. */
const ACCREDITATION_HANDLED = new Set([
	'questionnaire',
	'signatoryData',
	'accreditationType',
	'url',
	'status',
	'metaData',
]);

const isHttpUrl = (s: string | null): s is string => !!s && /^https?:\/\//i.test(s);

const accreditationFieldLabel = (key: string) =>
	ACCREDITATION_FIELD_LABEL[key] ??
	key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, c => c.toUpperCase());

const AccreditationRecord: FC<{
	entry: any;
	index: number;
	count: number;
	tierLabel: string | null;
	onResend?: (envelopeId: string) => Promise<boolean>;
}> = ({ entry, index, count, tierLabel, onResend }) => {
	const [sending, setSending] = useState(false);
	const {
		questionnaire = [],
		signatoryData,
		status,
		url,
		envelopeStatus,
		envelopeId,
	} = entry ?? {};

	const attestation = val(url);
	const envelope = val(envelopeId);
	const underReview = val(envelopeStatus) === 'underReview';

	// Old panel: an answer that names a third party reads as what it means --
	// the letter has been SENT to that party -- rather than as a bare noun.
	const questionRows: [string, any][] = (Array.isArray(questionnaire) ? questionnaire : [])
		.map((q: any) => {
			const label = val(q?.questions) ?? val(q?.key);
			const answer = val(q?.answers);
			const shown =
				answer && Signatory[answer]
					? `Accreditation letter sent to ${answer} for signing`
					: answer;
			return label ? ([label, shown] as [string, any]) : null;
		})
		.filter(Boolean) as [string, any][];
	const askedLabels = new Set(questionRows.map(([label]) => label.toLowerCase()));

	const signatoryName = signatoryData
		? [signatoryData.firstName, signatoryData.lastName].filter(Boolean).join(' ')
		: '';

	const rows: [string, any][] = [
		['Accreditation type', index === 0 ? tierLabel : null],
		...questionRows,
		['Signatory', signatoryName || null],
		// the questionnaire usually already asked this; don't print it twice
		['Signatory type', askedLabels.has('signatory type') ? null : signatoryData?.type ?? null],
		['Signatory email', signatoryData?.email ?? null],
		[
			'Packet status',
			underReview ? (
				<span className="a">
					Incomplete
					{!!envelope && !!onResend && (
						<button
							type="button"
							className="docact__btn acc-resend"
							disabled={sending}
							onClick={async () => {
								setSending(true);
								await onResend(envelope);
								setSending(false);
							}}
						>
							{sending ? 'Sending…' : 'Send document for signing'}
						</button>
					)}
				</span>
			) : status ? (
				<span className={status === 'completed' ? 'g' : 'a'}>{status}</span>
			) : null,
		],
		// A sentence in `url` is the packet's state, not a document.
		['Document', attestation && !isHttpUrl(attestation) ? attestation : null],
	];

	// Everything else the step recorded, under the old panel's names.
	Object.keys(entry ?? {})
		.filter(key => !ACCREDITATION_HANDLED.has(key) && !(key === 'envelopeStatus' && underReview))
		.forEach(key => {
			const raw = entry[key];
			if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
				const inner = val(raw.label) ?? val(raw.value) ?? val(raw);
				if (inner) rows.push([accreditationFieldLabel(key), inner]);
				return;
			}
			const shown =
				key === 'createdAt' || key === 'updatedAt' ? dayTime(raw) : val(raw);
			if (key === 'csvUrl' && isHttpUrl(shown)) {
				rows.push([
					accreditationFieldLabel(key),
					<a
						key={key}
						className="docact__btn"
						href={shown}
						target="_blank"
						rel="noreferrer"
					>
						Download CSV
					</a>,
				]);
				return;
			}
			if (shown) rows.push([accreditationFieldLabel(key), shown]);
		});

	return (
		<>
			{count > 1 && (
				<div className="acc-doc__note acc-doc__index">
					Document {index + 1} of {count}
				</div>
			)}
			<KV rows={rows} />
			{isHttpUrl(attestation) && (
				<>
					<DocActions
						url={attestation}
						downloadUrl={val(entry?.downloadUrl) || undefined}
						filename={`accreditation-${val(entry?.source) || 'attestation'}.pdf`}
						label="Signed accreditation attestation"
					/>
					<div className="acc-doc__note">
						{signatoryData?.email
							? `attested by ${signatoryData.email}`
							: 'self-attested'}
					</div>
				</>
			)}
		</>
	);
};

const AccreditationSection: FC<{
	payload: any;
	action?: IStepAction;
	onResend?: (envelopeId: string) => Promise<boolean>;
}> = ({ payload, action, onResend }) => {
	const entries: any[] = Array.isArray(payload?.data)
		? payload.data.filter(Boolean)
		: payload?.data
		? [payload.data]
		: [];
	if (!entries.length) return null;

	const first = entries[0];
	const accreditationType = first?.accreditationType;
	/* The tier CODE for the title -- `Accreditation(506c)`, as before. The
	   payload stamps `accreditationType` on entry [0] only; `source`/`type`
	   are the fallbacks the old panel read. */
	const tierCode =
		val(accreditationType?.value) ??
		val(first?.source) ??
		val(first?.type);
	const tierLabel =
		val(accreditationType?.label) ?? val(accreditationType?.value) ?? val(first?.source);
	const status = val(first?.status);

	return (
		<Section
			title={`Accreditation${tierCode ? ` (${tierCode})` : ''}`}
			right={status ? `Packet status : ${status}` : undefined}
		>
			<StepStatus action={action} />
			{entries.map((entry, i) => (
				<AccreditationRecord
					key={val(entry?._id) ?? val(entry?.id) ?? String(i)}
					entry={entry}
					index={i}
					count={entries.length}
					tierLabel={tierLabel}
					onResend={onResend}
				/>
			))}
		</Section>
	);
};

/* ------------------------------------------------------------------- KYB */

const KYB_FIELDS: [string, (r: any) => any][] = [
	/* THE COMPANY'S NAME, which this block did not show on any path.
	   Both KYB routes put it in the same place -- kyb.api normalises a
	   hand-entered company into the identical `enhanced.report` shape the
	   provider path produces -- so `basicInfo.name` is always there and was
	   simply never read. The result was a KYB panel listing a registration
	   number, an address and a TIN for a company it never named. */
	['Company name', r => r?.basicInfo?.name],
	['Registration date', r => r?.basicInfo?.registrationDate],
	['Registration number', r => r?.basicInfo?.registrationNumber],
	['Status', r => r?.basicInfo?.status],
	['Address', r => r?.basicInfo?.fullCompAddress ?? r?.basicInfo?.location?.value?.address],
	['TIN', r => r?.basicInfo?.metadata?.taxNumber ?? r?.basicInfo?.fein],
	['Country of Formation', r => r?.basicInfo?.location?.value?.country ?? r?.basicInfo?.metadata?.jurisdictionCode],
	['Date of Formation', r => r?.basicInfo?.metadata?.incorporationDate],
	['State of Formation', r => r?.basicInfo?.location?.value?.state],
	['Company type', r => r?.basicInfo?.metadata?.companyType],
	['Telephone number', r => r?.basicInfo?.contactDetails?.value?.phone],
	['Website link', r => r?.basicInfo?.contactDetails?.value?.website],
];

/**
 * A representative's per-check status, told honestly.
 *
 * Returns null (row dropped) when there is no status at all, "Not requested"
 * when the flow never ran that check, and the status itself otherwise.
 */
const NOT_STARTED = new Set(['pending', 'notstarted', 'not_started', 'none', '']);

const repStatus = (raw: any, ran?: boolean) => {
	const v = val(raw);
	if (!v) return null;
	const isIdle = NOT_STARTED.has(String(v).toLowerCase().replace(/\s+/g, ''));
	if (!ran && isIdle) return <span className="gy">Not requested</span>;
	return <span className={v === 'completed' ? 'g' : 'a'}>{v}</span>;
};

const KybSection: FC<{
	payload: any;
	action?: IStepAction;
	/** Does this FLOW actually run KYC / AML on the representatives? */
	ranKyc?: boolean;
	ranAml?: boolean;
}> = ({ payload, action, ranKyc, ranAml }) => {
	const entry = payload?.data?.[0]?.data ?? payload?.data?.data ?? null;
	const details = entry?.companyDetails ?? null;

	const tabs = useMemo(
		() =>
			Object.entries(details ?? {})
				.filter(([, v]: [string, any]) => v && typeof v === 'object' && v.report)
				.map(([k, v]: [string, any]) => ({ key: k, label: v.label ?? pretty(k) })),
		[details]
	);
	const [tab, setTab] = useState<string>('');
	const active = tab || tabs[0]?.key || '';

	if (!entry) return null;

	const report = details?.[active]?.report ?? {};
	/* REPRESENTATIVES COME FROM THE REPORT unless KYC/AML linked them.
	   `linkRepresentatives` is written ONLY inside the
	   representative_kyc_aml_verification branch, so on any flow that does not
	   BUY that check it is `[]` and this section rendered no rows at all --
	   for provider-sourced companies as much as hand-entered ones. The
	   report's `authorizedRepresentative` is present on both paths and is the
	   roster the old page used, so it is the right fallback: prefer the linked
	   records when they exist (they carry the KYC/AML verdicts), otherwise
	   show who the company actually declared. */
	/* THE ROSTER IS UNWRAPPED BY HAND, because val() cannot do it.
	 *
	 * val() is a display-stringifier: it always returns a string or null, and
	 * given {value: [ {...} ]} it recurses into the array and joins it, so the
	 * roster arrived as the literal "[object Object]". Two failures came out of
	 * routing this list through it, in order:
	 *
	 *   1. Passing that string to reps.map() threw inside render, React unmounted
	 *      the tree, and the operator got a COMPLETELY BLANK PAGE -- not a broken
	 *      section, the whole panel. Every KYB session rendered nothing at all.
	 *   2. Coercing the non-array away fixed the blank page but not the cause:
	 *      the roster was still a string, so it coerced to [] and the section
	 *      rendered no representatives -- silently, because a string is a
	 *      perfectly ordinary thing for val() to return.
	 *
	 * So unwrap the {value} envelope directly and keep the objects. The array
	 * checks below are the unwrap itself, not padding around a wrong source. */
	const wrapped: any = report?.authorizedRepresentative;
	const fromReport: any[] = Array.isArray(wrapped)
		? wrapped
		: Array.isArray(wrapped?.value)
			? wrapped.value
			: wrapped?.value && typeof wrapped.value === 'object'
				? [wrapped.value]
				: [];
	/* Array.isArray, not `.length` -- a STRING has a length too, and would sail
	   through into reps.map() and blank the whole panel. That is not
	   hypothetical here: rendering this section from a non-array is exactly how
	   every KYB session came back blank once already. */
	const reps: any[] = Array.isArray(entry?.linkRepresentatives)
		&& entry.linkRepresentatives.length
		? entry.linkRepresentatives
		: fromReport;
	const sanction = details?.businessSanction?.report;

	const rows = KYB_FIELDS.map(([label, pick]) => [label, val(pick(report))] as [string, any]);

	return (
		<Section title="KYB">
			<StepStatus action={action} />
			{tabs.length > 1 && (
				<div className="ktabs">
					{tabs.map(t => (
						<span
							key={t.key}
							className={`ktab${t.key === active ? ' on' : ''}`}
							onClick={() => setTab(t.key)}
						>
							{t.label}
						</span>
					))}
				</div>
			)}
			<KV rows={rows} />

			{reps.length > 0 && (
				<>
					<div className="grp">
						{reps.length > 1 ? 'Authorised Representatives' : 'Representative'}
					</div>
					{reps.map((rep, i) => (
						<KV
							// eslint-disable-next-line react/no-array-index-key
							key={rep?.representativeId ?? i}
							rows={[
								['Name', val(rep?.fullName)],
								/* TYPE AND DESIGNATION ARE DIFFERENT FACTS and were sharing
								   one row. `type` is the AUTHORITY the person holds —
								   ownerAndSigner, signer, owner — which is what a reviewer
								   is actually judging; `designation` is their job title.
								   Collapsed into one row, a representative with a title
								   showed the title and hid the authority entirely. */
								['Representative type', pretty(val(rep?.type) ?? '')],
								['Designation/Position', val(rep?.designation)],
								['Email', val(rep?.email)],
								/* val(), like every sibling row. The provider roster carries a
								   bare number here and the report's carries `{value: 100}`,
								   so interpolating it raw printed "[object Object]%" on the
								   path this fallback now makes reachable. */
								[
									'Ownership',
									val(rep?.ownershipPercentage) != null
										? `${val(rep?.ownershipPercentage)}%`
										: null,
								],
								/* "PENDING" IS A CLAIM THAT SOMETHING IS WAITING.
								   A representative's kycStatus/amlStatus sits at its
								   initial value forever when the flow never asks for that
								   check — so a KYB-only journey showed "pending" against
								   every representative and read as work outstanding, when
								   nothing had been requested of them at all. Shown as the
								   status only when the flow actually runs the check;
								   otherwise stated as not requested, which is the fact. */
								['KYC status', repStatus(rep?.kycStatus, ranKyc)],
								['AML status', repStatus(rep?.amlStatus, ranAml)],
							]}
						/>
					))}
				</>
			)}

			{sanction && (
				<>
					<div className="grp">Business Sanctions/OFAC</div>
					<KV
						rows={[
							[
								'Sanctions screening',
								sanction?.subcases?.value?.length ? (
									<span className="rd">
										{sanction.subcases.value.length} potential match
										{sanction.subcases.value.length > 1 ? 'es' : ''}
									</span>
								) : (
									<span className="g">No matches</span>
								),
							],
						]}
					/>
				</>
			)}
		</Section>
	);
};

/* ------------------------------------------------------------------- AML */

/** A screening verdict that is CLEAR — anything else is worth a reviewer's eye. */
const AML_CLEAR = /^(no match|passed|clear|low|none|false positive|not applicable)$/i;

/**
 * The RESULT-level verdict adds the providers' pass words. `Approved` is what
 * Didit returns for a screening that found nothing, and it was rendering RED —
 * the single most reassuring word in the panel, coloured as an alarm. A green
 * "Actionable hits: None" sat directly beneath it, so the section contradicted
 * itself and the louder half was wrong.
 */
const AML_RESULT_CLEAR = /^(approved|accepted|pass(ed)?|completed)$/i;

/**
 * Colour a verdict by whether it is clear, not by whether a string is present.
 *
 * `scope` matters and is not cosmetic. On the RESULT, "approved" means the
 * screening cleared the person. On a single HIT, `review_status: approved`
 * means a reviewer accepted that hit as a true match — the opposite reading —
 * so the pass words are deliberately not applied there.
 */
const amlVerdict = (v: any, scope: 'result' | 'hit' = 'result') => {
	const t = String(val(v) ?? '').trim();
	if (!t) return null;
	const clear =
		AML_CLEAR.test(t) || (scope === 'result' && AML_RESULT_CLEAR.test(t));
	return <span className={clear ? 'g' : 'rd'}>{t}</span>;
};

/** A scalar or flat array as text. NEVER returns an object — an object reaching
 *  React as a child throws "Objects are not valid as a React child" and blanks
 *  the whole drawer, which reads as a page that failed to load. */
const amlText = (v: any): string | null => {
	if (v === null || v === undefined) return null;
	if (Array.isArray(v)) {
		const parts = v.map(amlText).filter(Boolean);
		return parts.length ? parts.join(', ') : null;
	}
	if (typeof v === 'object') return null;
	const t = String(v).trim();
	return !t || t === 'MISSING_KEY' ? null : t;
};

/** Humanise a camelCase / dotted payload path for display. */
const amlLabel = (path: string) =>
	(path.split('.').pop() ?? '')
		.replace(/\[\]$/, '')
		.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
		.replace(/^./, c => c.toUpperCase());

/**
 * VENDOR NAMES ARE NOT SHOWN IN REPORTS.
 *
 * A reviewer reads a screening result; which vendor produced it is our
 * procurement detail, and naming it on a compliance record invites the reader to
 * weigh the finding by the brand behind it. It also dates the document — the
 * vendor changes and the archived report still names the old one.
 *
 * Stripped centrally rather than per-row, because amlRest() sweeps up every
 * unnamed leaf: dropping the explicit row alone would let the catch-all print it
 * straight back, and the same would happen for any future payload that carries a
 * provider field under a different name.
 */
const VENDOR_KEYS = new Set([
  'provider', 'providerName', 'vendor', 'vendorData', 'vendor_data',
]);

/**
 * EVERY REMAINING LEAF, so nothing the screening returns is silently dropped.
 *
 * A hand-written field list always lags the provider: AML Watcher's payload
 * already carries fields this screen never named, and a screening provider adds
 * to its response far more often than we revisit this file. Anything not
 * explicitly rendered above is collected here instead of disappearing — an
 * unlabelled row a reviewer can still read beats a value they are never shown.
 */
const amlRest = (obj: any, skip: Set<string>): [string, ReactNode][] => {
	const rows: [string, ReactNode][] = [];
	/* Compare skip paths WITHOUT array indices, so one entry suppresses a field
	   across every element: `matchedNames[0].score` and `matchedNames[7].score`
	   both normalise to `matchedNames.score`. Without this, a field rendered
	   explicitly above was ALSO emitted here for each array element, and the
	   reviewer saw the same value twice under two different labels. */
	const norm = (path: string) => path.replace(/\[\d+\]/g, '');
	const rows_seen = new Set<string>();
	const walk = (o: any, path: string) => {
		if (o === null || o === undefined) return;
		/* Check the skip BEFORE descending. Checking it only at the leaves meant
		   an excluded array-of-objects (`results`, `adverseMedia`) was walked
		   anyway and every field of every record was re-emitted into the parent
		   block — the reviewer saw each hit twice, once properly formatted and
		   once as a flat list of unlabelled duplicates. */
		if (skip.has(norm(path))) return;
		if (Array.isArray(o)) {
			if (!o.length) return;
			if (o.every(x => typeof x !== 'object' || x === null)) {
				if (skip.has(norm(path))) return;
				const t = amlText(o);
				if (t) rows.push([amlLabel(path), t]);
				return;
			}
			o.forEach((x, i) => walk(x, `${path}[${i}]`));
			return;
		}
		if (typeof o === 'object') {
			Object.keys(o).forEach(k => walk(o[k], path ? `${path}.${k}` : k));
			return;
		}
		if (skip.has(norm(path))) return;
		if (VENDOR_KEYS.has(norm(path).split('.').pop() ?? '')) return;
		const t = amlText(o);
		if (t === null) return;
		/* Identical label AND value from two array elements is noise, not data. */
		const label = amlLabel(path);
		const key = `${label}\u0000${t}`;
		if (rows_seen.has(key)) return;
		rows_seen.add(key);
		rows.push([label, t]);
	};
	walk(obj, '');
	return rows;
};

const AmlSection: FC<{ payload: any; action?: IStepAction }> = ({ payload, action }) => {
	const entry = payload?.data?.[0] ?? payload?.data ?? null;
	if (!entry) return null;

	/* DIDIT ONLY. AML Watcher is gone from the platform, and this section used to
	 * print its vocabulary — caseStatus, matchStatus, riskLevel, searchReference,
	 * totalRecords, languageCode — unconditionally. None of those exist on a
	 * Didit screening, so every one rendered as a labelled row with nothing
	 * beside it: eight blank rows on every session. */
	const d = entry?.data ?? {};
	const q = entry?.payload ?? {};

	const hits: any[] = Array.isArray(d?.hits) ? d.hits : [];

	/* Didit tags a hit False Positive when it scored below the match-score
	   threshold, so it is audit trail rather than a finding. Counting those would
	   flag every common name. */
	const actionable = hits.filter(h => h?.review_status !== 'False Positive');
	const confirmed = actionable.filter(
		h => String(h?.review_status ?? '').toLowerCase() === 'confirmed match'
	).length;

	/* What the hits are ON — a sanctions match reads differently from adverse
	   media, and a reviewer should see which without opening every card. */
	const datasets = Array.from(
		new Set(
			actionable.flatMap(h => (Array.isArray(h?.datasets) ? h.datasets : []))
		)
	);
	const sanctionHit = datasets.some(x => /sanction/i.test(String(x)));

	/* Rendered explicitly below, so the catch-all does not print them twice.
	   `hits` in particular: it was NOT in this set before, so every hit's fields
	   were flattened into the summary block — one screening produced a wall of
	   unlabelled rows with the same field names repeating per hit. */
	const shown = new Set([
		'status', 'total_hits', 'score', 'entity_type', 'hits', 'warnings',
		'is_ongoing_monitoring_enabled', 'is_fixture',
		'screened_data.full_name', 'screened_data.date_of_birth',
		'screened_data.nationality', 'screened_data.document_number',
	]);
	const qShown = new Set([
		'name', 'type', 'dob', 'doi', 'documentNumber', 'onGoing',
		'clientReference', 'nationality',
	]);

	return (
		<Section
			title="PEPs &amp; Sanctions"
			right={entry?.status ? `Status : ${entry.status}` : undefined}
		>
			<StepStatus action={action} />

			{/* THE FINDING, BEFORE ANYTHING ELSE.
			    A screening that matched a sanctions list is the most consequential
			    thing this drawer can say, and it was a number in a row two thirds
			    of the way down. Named, coloured, and first. */}
			{!!actionable.length && (
				<div className={`amlwarn${sanctionHit ? ' amlwarn--sanction' : ''}`}>
					<span className="amlwarn__t">
						{sanctionHit ? 'Sanctions match' : 'Screening match'}
					</span>
					<span className="amlwarn__d">
						{actionable.length} actionable{' '}
						{actionable.length === 1 ? 'hit' : 'hits'}
						{confirmed > 0 && ` · ${confirmed} confirmed`}
						{!!datasets.length && ` · ${datasets.join(', ')}`}
					</span>
					<span className="amlwarn__n">
						Not cleared until a compliance agent rules on it above.
					</span>
				</div>
			)}

			{/* WHAT WAS SCREENED — half the evidence. A clean result means nothing
			    until you know which identity produced it, and this is what exposes
			    a mismatch between the identity screened and the one on file. */}
			<div className="grp">Identity screened</div>
			<KV
				rows={[
					[
						'Name',
						amlText(q.name) ?? amlText(d?.screened_data?.full_name),
					],
					['Entity type', amlText(q.type) ?? amlText(d.entity_type)],
					[
						'Date of birth',
						amlText(q.dob) ?? amlText(d?.screened_data?.date_of_birth),
					],
					['Date of incorporation', amlText(q.doi)],
					[
						'Nationality',
						amlText(q.nationality) ?? amlText(d?.screened_data?.nationality),
					],
					[
						'Document number',
						amlText(q.documentNumber) ??
							amlText(d?.screened_data?.document_number),
					],
					[
						'Ongoing monitoring',
						typeof q.onGoing === 'boolean'
							? q.onGoing
								? 'Yes'
								: null
							: amlText(q.onGoing),
					],
					['Screened at', val(entry?.createdAt) ? dayTime(entry.createdAt) : null],
					...amlRest(q, qShown),
				]}
			/>

			<div className="grp">Result</div>
			<KV
				rows={[
					['Verdict', amlVerdict(d.status)],
					[
						'Actionable hits',
						actionable.length ? (
							<span className="rd">{actionable.length}</span>
						) : (
							<span className="g">None</span>
						),
					],
					/* Only when there are some — a "0 false positives" row is noise. */
					['False positives', hits.length - actionable.length || null],
					['Risk score', amlText(d.score)],
					['Lists matched', datasets.length ? datasets.join(', ') : null],
					[
						'Ongoing monitoring',
						d.is_ongoing_monitoring_enabled === true ? 'Enabled' : null,
					],
					['Warnings', amlText(d.warnings)],
					...amlRest(d, shown),
				]}
			/>

			{/* EVERY HIT AS ITS OWN CARD, with the nested detail behind it — the
			    PEP entry, the sanctions programme, the enforcement case, the
			    article. A score with no source is not reviewable. */}
			{hits.map((h: any, i: number) => {
				const fp =
					String(h?.review_status ?? '').toLowerCase() === 'false positive';
				return (
					// eslint-disable-next-line react/no-array-index-key
					<div key={h?.id ?? i} className={fp ? 'amlhit amlhit--fp' : 'amlhit'}>
						<div className="grp">
							{amlText(h?.caption) || `Hit ${i + 1}`}
							{h?.review_status ? ' — ' : ''}
							{amlVerdict(h?.review_status, 'hit')}
						</div>
						<KV
							rows={[
								['Lists', amlText(h?.datasets)],
								['Match score', amlText(h?.match_score)],
								['Risk score', amlText(h?.risk_score)],
								['Also known as', amlText(h?.properties?.name)],
								['Aliases', amlText(h?.properties?.alias)],
								['Date of birth', amlText(h?.properties?.birthDate)],
								['Place of birth', amlText(h?.properties?.birthPlace)],
								['Nationality', amlText(h?.properties?.nationality)],
								['Position', amlText(h?.properties?.position)],
								['Notes', amlText(h?.properties?.notes)],
								['First seen', amlText(h?.first_seen)],
								['Last seen', amlText(h?.last_seen)],
								['Source', amlText(h?.url)],
								['Summary', amlText(h?.additional_information?.flag_summary)],
								...amlRest(h?.pep_matches ?? [], new Set()),
								...amlRest(h?.sanction_matches ?? [], new Set()),
								...amlRest(h?.warning_matches ?? [], new Set()),
								...amlRest(h?.adverse_media_matches ?? [], new Set()),
								...amlRest(h?.linked_entities ?? [], new Set()),
								...amlRest(h?.score_breakdown ?? {}, new Set()),
							]}
						/>
					</div>
				);
			})}
		</Section>
	);
};

/* ------------------------------ KYB members & executives PEPs & sanctions */

/**
 * Every member and executive added during KYB, screened individually against
 * PEPs, sanctions and watchlists.
 *
 * WHY IT IS ITS OWN SECTION rather than rows inside KYB: a sanctions hit on a
 * beneficial owner is not a KYB field. Folded into the company report it would
 * sit below thirty registry rows a reviewer skims, and the one finding that
 * needs acting on would be the least visible thing on the panel.
 *
 * THE ORDERING IS THE POINT. Hits first, then people who could NOT be screened,
 * then the clear majority as one quiet line each. Five clear people must never
 * look like five problems, and the person with a hit has to be findable in
 * about a second.
 *
 * NOT SCREENED IS NOT CLEAR. A member the registry gave no usable name for was
 * never searched and was never billed for. It is drawn amber and dashed, counted
 * apart from the clears in both the chip and the band, and its card says so in
 * words — rendering "no hits" for somebody nobody looked up would be the most
 * dangerous thing this section could do.
 */
const MembersOfacSection: FC<{ payload: any }> = ({ payload }) => {
	const entry = payload?.data?.[0]?.data ?? payload?.data?.data ?? null;
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const people: any[] = Array.isArray(entry?.membersOfac) ? entry.membersOfac : [];

	const [open, setOpen] = useState<Set<string>>(new Set());
	const toggle = useCallback(
		(id: string) =>
			setOpen(prev => {
				const next = new Set(prev);
				// eslint-disable-next-line @typescript-eslint/no-unused-expressions
				next.has(id) ? next.delete(id) : next.add(id);
				return next;
			}),
		[]
	);

	/* Bucketed once, and by the STORED verdict rather than by re-deriving one
	   from the hits — the backend already discounted the vendor's own false
	   positives when it decided `status`, and a second opinion here would let
	   the chip and the card disagree. */
	const { hit, dead, clear } = useMemo(() => {
		const h: any[] = [];
		const d: any[] = [];
		const c: any[] = [];
		people.forEach(p => {
			if (!p?.screened) d.push(p);
			else if (p?.status === 'potential_match') h.push(p);
			else c.push(p);
		});
		return { hit: h, dead: d, clear: c };
	}, [people]);

	/* The option was never bought, or the step has not completed — the backend
	   writes this array (empty included) only when it actually ran. Nothing to
	   say, so nothing is drawn: not a header, not an empty shell. */
	if (!Array.isArray(entry?.membersOfac)) return null;

	const screened = people.length - dead.length;

	const chip = hit.length
		? { v: 'rej' as const, t: `${hit.length} HIT${hit.length > 1 ? 'S' : ''}` }
		: !people.length
		? { v: 'ns' as const, t: 'NO ONE TO SCREEN' }
		: dead.length
		? { v: 'rev' as const, t: 'PARTIAL' }
		: { v: 'ok' as const, t: 'CLEAR' };

	/* One claim for the whole roster. Green is reserved for everyone screened
	   AND nothing matched — anything less is amber, so a partially-screened
	   roster can never be read at a glance as a clean one. */
	const band = hit.length
		? {
				v: 'hit',
				t: (
					<>
						<span className="cnt">{hit.length}</span> of {people.length} matched a
						sanctions or PEP list
					</>
				),
				d: 'Every member and executive added during KYB was screened individually against PEP, sanctions and watchlist data at step completion.',
				n: `${clear.length} clear · ${dead.length} not screened — a person who was not screened is not clear.`,
		  }
		: !people.length
		? {
				v: 'dead',
				t: <>Screening on — no people added</>,
				d: 'The KYB step recorded no members or executives, so nothing was screened and nothing was billed.',
				n: 'If this business has officers on record, the KYB roster is incomplete — that is the finding here, not a clear.',
		  }
		: dead.length
		? {
				v: 'open',
				t: (
					<>
						<span className="cnt">{screened}</span> of {people.length} screened —
						nothing matched
					</>
				),
				d: 'No PEP, sanctions or watchlist entry matched anyone who could be searched.',
				n: `${dead.length} could not be screened at all. That is not a clear result for them.`,
		  }
		: {
				v: 'clear',
				t: (
					<>
						<span className="cnt">{people.length}</span> screened — nothing matched
					</>
				),
				d: 'Every member and executive added during KYB was screened; no PEP, sanctions or watchlist entry matched any of them.',
				n: 'Clear is a no-match on the attributes the registry supplied — not identity verification.',
		  };

	/* The stored verdict in a reviewer's words. `clear` is deliberately "No
	   match" and not "Approved": nobody approved anyone here, a list simply did
	   not answer. Both words read green through amlVerdict; only one of them is
	   a claim we can defend. */
	const verdictWord = (p: any) =>
		p?.status === 'potential_match'
			? 'Potential match'
			: p?.status === 'clear'
			? 'No match'
			: 'No result returned';

	const roleOf = (p: any) =>
		[
			pretty(amlText(p?.type) ?? ''),
			amlText(p?.designation),
			p?.ownershipPercentage ? `${p.ownershipPercentage}%` : null,
		]
			.filter(Boolean)
			.join(' · ');

	/* WHO WAS SEARCHED, on every card. A result printed without the identity
	   behind it is unreadable — the same mistake as reading a clean record for
	   "Sandbox User" as a clean record for the applicant. */
	const identity = (p: any) => (
		<div className="mo-id">
			<b>Screened as</b>
			{p.name} — the name on the registry record. No date of birth was available,
			so the search was run on the name alone.
		</div>
	);

	const card = (p: any, i: number) => {
		const hits: any[] = Array.isArray(p?.matches) ? p.matches : [];
		const actionable = hits.filter(
			h => String(h?.review_status ?? '').toLowerCase() !== 'false positive'
		);
		const isHit = p?.status === 'potential_match';
		return (
			<div
				className={`mo-rec${isHit ? '' : ' mo-rec--clear'}`}
				key={p?.representativeId ?? `${p?.name}-${i}`}
			>
				<div className="mo-rec__hd">
					<span className="mo-rec__name">{p.name}</span>
					{roleOf(p) && <span className="mo-role">{roleOf(p)}</span>}
					<span
						className={`mo-rec__tag mo-rec__tag--${isHit ? 'sanction' : 'clear'}`}
					>
						{isHit ? 'POTENTIAL MATCH' : 'CLEAR'}
					</span>
				</div>
				{identity(p)}
				<KV
					rows={[
						['Verdict', amlVerdict(verdictWord(p))],
						[
							'Actionable hits',
							actionable.length ? (
								<span className="rd">{actionable.length}</span>
							) : (
								<span className="g">None</span>
							),
						],
						/* Only when there are some — a "0 false positives" row is noise. */
						['Discounted as false positives', hits.length - actionable.length || null],
						['Lists matched', p?.lists?.length ? p.lists.join(', ') : null],
						[
							'Also verified separately',
							p?.hasOwnKycLink ? 'Yes — sent their own KYC link' : null,
						],
						['Screened at', p?.screenedAt ? dayTime(p.screenedAt) : null],
					]}
				/>

				{/* EVERY HIT ITS OWN BLOCK. Flattened together, one hit's date of
				    birth reads as another's — the exact failure the primary AML
				    section was rebuilt to avoid. */}
				{hits.map((h: any, hi: number) => {
					const fp =
						String(h?.review_status ?? '').toLowerCase() === 'false positive';
					const score = Number(h?.match_score) || 0;
					return (
						<div
							// eslint-disable-next-line react/no-array-index-key
							key={h?.id ?? hi}
							className={fp ? 'mo-hit mo-hit--fp' : 'mo-hit'}
						>
							<div className="mo-hit__hd">
								{amlText(h?.caption) || `Hit ${hi + 1}`}
								{h?.review_status ? ' — ' : ''}
								{amlVerdict(h?.review_status, 'hit')}
							</div>
							<div className="mo-sets">
								{(Array.isArray(h?.datasets) ? h.datasets : []).map(
									(ds: string) => (
										<span className="mo-b" key={ds}>
											{ds}
										</span>
									)
								)}
								{!!score && (
									<span className="mo-conf">
										<span className="mo-conf__bar">
											<i
												style={{
													width: `${Math.min(100, score <= 1 ? score * 100 : score)}%`,
												}}
											/>
										</span>
										<span className="mo-conf__n">
											{score <= 1 ? Math.round(score * 100) : Math.round(score)}
										</span>
									</span>
								)}
							</div>
							<KV
								rows={[
									['Lists', amlText(h?.datasets)],
									['Risk score', amlText(h?.risk_score)],
									['Also known as', amlText(h?.properties?.name)],
									['Aliases', amlText(h?.properties?.alias)],
									['Date of birth', amlText(h?.properties?.birthDate)],
									['Place of birth', amlText(h?.properties?.birthPlace)],
									['Nationality', amlText(h?.properties?.nationality)],
									['Position', amlText(h?.properties?.position)],
									['Notes', amlText(h?.properties?.notes)],
									['Source', amlText(h?.url)],
									[
										'Summary',
										amlText(h?.additional_information?.flag_summary),
									],
								]}
							/>
						</div>
					);
				})}

				{/* A finding with no next step only raises anxiety. */}
				{isHit && (
					<div className="mo-next">
						<b>A match is a lead, not a verdict.</b> The name matched a list entry
						on the attributes the registry supplied; no document was checked
						against it. A compliance reviewer decides whether this is the same
						person before the session is approved.
					</div>
				)}
			</div>
		);
	};

	return (
		<Section
			title="PEPs & Sanctions — Executives & Members"
			right={
				<>
					<StatusChip verdict={chip.v}>{chip.t}</StatusChip>
					<span className="cbc-meta">
						{people.length} {people.length === 1 ? 'person' : 'people'} ·{' '}
						{screened} screened
					</span>
				</>
			}
		>
			<div className={`mo-band mo-band--${band.v}`}>
				<span className="mo-band__t">{band.t}</span>
				<span className="mo-band__d">{band.d}</span>
				<span className="mo-band__n">{band.n}</span>
			</div>

			{/* A standing condition on the section, deliberately outside the verdict
				palette so it never trains a reviewer to skim amber. */}
			<div className="mo-scope">
				<b>A name match, not identity</b>
				This screens the names the KYB registry supplied against PEP, sanctions and
				watchlist data. No document was checked, and members rarely have a date of
				birth on record — so the search is broader and its matches weaker than the
				applicant&apos;s own AML screen. A clear result means no list entry matched
				that name, not that the person is who the registry says.
			</div>
			<div className="mo-src">
				Identities come from the KYB registry record and the members and executives
				added during onboarding — not from verified ID documents.
			</div>

			{hit.length > 0 && <div className="grp">Matches — act on these first</div>}
			{hit.map((p, i) => card(p, i))}

			{dead.length > 0 && <div className="grp">Could not be screened</div>}
			{dead.map((p, i) => (
				<div className="mo-rec mo-rec--dead" key={p?.representativeId ?? `d${i}`}>
					<div className="mo-rec__hd">
						<span className="mo-rec__name">{p?.name || 'Unnamed member'}</span>
						{roleOf(p) && <span className="mo-role">{roleOf(p)}</span>}
						<span className="mo-rec__tag mo-rec__tag--dead">NOT SCREENED</span>
					</div>
					<div className="mo-id mo-id--thin">
						<b>Not searched</b>
						{p?.reason ?? 'no result was returned for this person'}.
					</div>
					<div className="mo-dead__d">
						Nothing was searched for this person, and nothing was billed for them.
					</div>
					<div className="mo-dead__n">
						This is not a clear result. Complete this member&apos;s record on the
						KYB step and re-run the screening.
					</div>
				</div>
			))}

			{clear.length > 0 && (
				<div className="grp">No match — {clear.length} · click to unfold</div>
			)}
			{clear.map((p, i) => {
				const id = String(p?.representativeId ?? `${p?.name}-${i}`);
				const isOpen = open.has(id);
				const hits: any[] = Array.isArray(p?.matches) ? p.matches : [];
				return (
					<Fragment key={id}>
						<div
							className="mo-row"
							role="button"
							tabIndex={0}
							onClick={() => toggle(id)}
							onKeyDown={e => {
								if (e.key === 'Enter' || e.key === ' ') toggle(id);
							}}
						>
							<span className="mo-row__ar">{isOpen ? '▼' : '▶'}</span>
							<span className="mo-row__n">{p?.name}</span>
							<span className="mo-row__t">{roleOf(p)}</span>
							<span className="mo-row__c">
								{hits.length
									? `${hits.length} below threshold · discounted`
									: '0 hits'}
							</span>
							<span className="mo-row__v">No match</span>
						</div>
						{isOpen && card(p, i)}
					</Fragment>
				);
			})}

			{/* THE PROVIDER IS NEVER NAMED, the same rule the AML section follows.
				What this run was and how often it happens is what a reviewer needs. */}
			<div className="mo-foot">
				One-time screening at KYB completion · no ongoing monitoring · option
				&ldquo;PEPs &amp; Sanctions - Executive &amp; Members&rdquo;
			</div>
		</Section>
	);
};

/* --------------------------------------------------------------- activity */

const DOT: Record<string, string> = {
	pass: 'dot-ok',
	review: 'dot-rev',
	active: 'dot-strt',
	fail: 'dot-rej',
	unreached: 'dot-pend',
	pending: 'dot-pend',
};

const STATE_WORD: Record<string, string> = {
	pass: 'completed',
	review: 'processing',
	active: 'in progress',
	fail: 'rejected',
	unreached: 'pending',
	pending: 'pending',
};

interface ISessionDrawer {
	session: any | null;
	open: boolean;
	onClose: () => void;
	onViewFlow: (session: any) => void;
	onDownload: (session: any, fileType: string) => void;
	/**
	 * A decision was recorded, so whatever else is drawn from this session's
	 * node statuses is now out of date.
	 *
	 * THE DRAWER CANNOT FIX ITSELF HERE. `session` is the ROW OBJECT, handed in
	 * by the deck as a snapshot taken when the row was clicked; the drawer's own
	 * reload refetches /complex-session-details and repaints its SECTIONS, but
	 * it cannot reach back into that snapshot. Everything drawn from the
	 * snapshot therefore kept the old colours: the chain pills at the top of the
	 * drawer, the chevron tree on the list row behind it, and the Tree Flow
	 * modal — which is handed the same object again.
	 *
	 * That is why a KYC step set to Rejected did not turn red, and an AML step
	 * set to Approved stayed red: the write and the drawer's own sections were
	 * correct all along, and only the views fed by the snapshot were stale.
	 */
	onChanged?: () => void;
}

export const SessionDrawer: FC<ISessionDrawer> = ({
	session,
	open,
	onClose,
	onViewFlow,
	onDownload,
	onChanged,
}) => {
	const {
		detail,
		steps,
		stepsAll,
		loaded,
		posting,
		comment,
		setStatus,
		setRisk,
		reviewers,
		assign,
		resendAccreditationInvite,
	} = useSessionDrawer(
		open && session ? session._id : null
	);

	const [text, setText] = useState('');
	const [sendMail, setSendMail] = useState(false);
	const [actTab, setActTab] = useState<'all' | 'comments'>('all');

	const chain = useMemo(() => (session ? toChain(session) : []), [session]);
	const verdict = useMemo(
		() => (session ? toVerdict(session, chain) : null),
		[session, chain]
	);

	/**
	 * The step a comment belongs to: whatever is holding the session up.
	 *
	 * A STEP ID WITHOUT A NODE ID IS REJECTED. pipelines.api answers
	 * `400 "Node id is required"` for exactly that pairing (activities.patch),
	 * and this used to fall back to a literal `'session'` when the session had
	 * no nodes to point at — a step id no node carries, sent with no node id.
	 * Every comment on such a session 400'd and the composer said "Could not add
	 * the comment", which reads as commenting being broken outright.
	 *
	 * Omitting BOTH is the supported shape for a note that belongs to the
	 * session rather than to one of its steps (verified: 200), so the pair is
	 * kept together — either a real node's stepId with its own _id, or neither.
	 */
	const currentStep = useMemo(() => {
		const nodes: any[] = detail?.nodes ?? session?.nodes ?? [];
		const stuck = nodes.find((n: any) => (n?.action?.status ?? n?.status) === 'processing');
		const visited = [...nodes].reverse().find((n: any) => n?.visited);
		const node = stuck ?? visited ?? nodes[0];
		if (!node?.stepId || !node?._id) return {};
		return { stepId: node.stepId, nodeId: node._id };
	}, [detail, session]);

	/**
	 * One decision control per drawn step. `nodeId` matters: pipelines.api
	 * matches on {_id: nodeId, session, stepId} and answers 400 "Session node
	 * not found" if the pair disagrees, so the id has to come from the node
	 * carrying that stepId rather than from whatever step is current.
	 */
	const nodesByStep = useMemo(() => {
		const nodes: any[] = detail?.nodes ?? session?.nodes ?? [];
		const map: Record<string, any> = {};
		nodes.forEach((n: any) => {
			if (n?.stepId && !map[n.stepId]) map[n.stepId] = n;
		});
		return map;
	}, [detail, session]);

	/* `nodeId` names WHICH node when a step occurs twice. nodesByStep keeps the
	   first node per stepId, so without it the decision under the second
	   Accreditation packet would have written its status onto the FIRST node --
	   approving 506(c) would have flipped the 506(b) packet. */
	const stepAction = useCallback(
		(stepId: string, nodeId?: string): IStepAction | undefined => {
			const nodes: any[] = detail?.nodes ?? session?.nodes ?? [];
			const node = nodeId
				? nodes.find((n: any) => String(n?._id) === String(nodeId)) ?? nodesByStep[stepId]
				: nodesByStep[stepId];
			if (!node) return undefined;
			/**
			 * ON A 506(c), THIS BAR SHOWS THE AGENT'S DECISION — NOT THE NODE.
			 *
			 * Everywhere else the two are the same thing: the bar sets the node's
			 * status and reads it straight back. A 506(c) accreditation is the one
			 * place they come apart. The step genuinely finishes when the third
			 * party e-signs the attestation, so pipelines completes the node and
			 * the pill goes green — but the SESSION still needs a human, and
			 * nothing about that signature is a human decision.
			 *
			 * Reading the node here would therefore have shown "Approved" the
			 * instant the signature landed, on a session nobody had reviewed, and
			 * then eaten the agent's click: `onChange` ignores a value equal to
			 * the one already selected, so the only control that can release the
			 * session would have been a dead dropdown that already claimed to say
			 * what the agent wanted it to say.
			 *
			 * The session's own `complianceApproval` is the honest source, mapped
			 * back to the same three option values. Absent — nobody has ruled yet
			 * — is '', which the select already renders as "Needs a decision…".
			 */
			const isVerifiedAccreditation =
				stepId === 'accreditation' &&
				(node?.action?.data?.accreditationType?.value ??
					node?.action?.data?.accreditationType) === '506c';
			const nodeStatus = node?.action?.status ?? node?.status ?? '';
			const status = isVerifiedAccreditation
				? DECISION_STATUS[detail?.complianceApproval?.decision] ?? ''
				: nodeStatus;
			return {
				stepId,
				nodeId: node?._id,
				status,
				busy: posting,
				onChange: async (to: string) => {
					if (!to || to === status) return;
					const ok = await setStatus({
						to,
						from: status,
						stepId,
						nodeId: node?._id,
					});
					/* Only on success: a refresh after a failed write would repaint
					   the row with the SAME status and read as "the decision was
					   taken and then undone". */
					if (ok) onChanged?.();
				},
			};
		},
		[nodesByStep, detail, session, posting, setStatus, onChanged]
	);

	const activities = useMemo(() => {
		const all: any[] = detail?.activities ?? [];
		const list = actTab === 'comments' ? all.filter(a => a?.type === 'comment') : all;
		return [...list].sort(
			(a, b) => (b?.metadata?.time ?? 0) - (a?.metadata?.time ?? 0)
		);
	}, [detail, actTab]);

	const submit = useCallback(async () => {
		const ok = await comment(text, { sendMail, ...currentStep });
		if (ok) {
			setText('');
			setSendMail(false);
		}
	}, [comment, text, sendMail, currentStep]);

	const [dlOpen, setDlOpen] = useState(false);
	const [assignee, setAssignee] = useState('');
	const [assignNote, setAssignNote] = useState('');

	/** Whoever holds it now — the API returns either an id or an expanded user. */
	const assignedTo = useMemo(() => {
		const a: any = detail?.assignee ?? session?.assignee ?? null;
		if (!a) return '';
		if (typeof a === 'string')
			return (
				[
					reviewers.find((r: any) => r._id === a)?.firstName,
					reviewers.find((r: any) => r._id === a)?.lastName,
				]
					.filter(Boolean)
					.join(' ') || ''
			);
		return [a.firstName, a.lastName].filter(Boolean).join(' ') || a.email || '';
	}, [detail, session, reviewers]);

	const [lightbox, setLightbox] = useState<{
		src: string;
		label: string;
		kind: 'image' | 'video';
	} | null>(
		null
	);
	const openZoom = useCallback(
		(src: string, label: string, kind: 'image' | 'video' = 'image') =>
			setLightbox({ src, label, kind }),
		[]
	);

	/**
	 * Face-match confidence — the liveness capture scored against the photo on
	 * the government-issued document. It lives on the KYC node, not the
	 * authentication one, because that is the step holding both images.
	 *
	 * IT ARRIVES IN TWO SHAPES and reading only one is why this showed 0%:
	 *
	 *   simpliciMatchPercentage  a STRING like "97.00%", which is what Didit's
	 *                            sandbox returns on a document flow;
	 *   matchPercentage.similarity  a NUMBER, written when the flow has a
	 *                            liveness step and the captured selfie was
	 *                            compared against the document.
	 *
	 * The sandbox emits the first and never the second, so a drawer that only
	 * read `matchPercentage.similarity` reported every sandbox session as a 0%
	 * face match — a real finding-shaped number for a comparison that had in
	 * fact scored 97%. QA_test/qa-onboarding.py accepts either for exactly this
	 * reason; so does this.
	 *
	 * Both are read, and a non-zero score wins: when the raw field is present
	 * but zero purely because the registry leg never ran, the string is the one
	 * carrying the real answer.
	 */
	const similarity = useMemo(() => {
		const node = steps.kyc?.data?.kyc ?? steps.kyc?.kyc ?? steps.kyc?.data ?? null;
		const basic = node?.basicInfo ?? null;
		if (!node && !basic) return null;

		const toNum = (raw: any): number | null => {
			const text = val(raw);
			if (text === null || text === undefined) return null;
			const n = Number(String(text).replace('%', '').trim());
			return Number.isFinite(n) ? n : null;
		};

		/* PRODUCTION'S OWN ORDER FIRST. complex-kyc-details.tsx destructures
		   `similarity` straight off the KYC node's data and hands it to the
		   progress bar as `similarity ?? 0` — that is the authoritative field.
		   The rest are fallbacks for the shapes the sandbox emits instead. */
		const candidates = [
			toNum(node?.similarity),
			toNum(node?.report?.similarity),
			toNum(basic?.simpliciMatchPercentage),
			toNum(basic?.matchPercentage?.similarity),
			toNum(basic?.matchPercentage),
		].filter((n): n is number => n !== null);

		if (!candidates.length) return null;
		return candidates.find(n => n > 0) ?? candidates[0];
	}, [steps.kyc]);

	/* The current risk level. The legacy header reads the most recent
	   `priorityRisk` off the activity log and falls back to the session's own
	   field, because the log is the record of truth once anything has been set. */
	const currentRisk = useMemo(() => {
		const acts = (detail as any)?.activities ?? [];
		const latest = acts.find((a: any) => a?.metadata?.priorityRisk);
		return (
			latest?.metadata?.priorityRisk ??
			(session as any)?.priorityRisk ??
			'unassigned'
		);
	}, [detail, session]);

	/** The matching RISK_LEVELS entry, or null when nothing has been set yet. */
	const riskLevel = useMemo(
		() => RISK_LEVELS.find(l => l.value === currentRisk) ?? null,
		[currentRisk]
	);

	if (!session) return <Drawer open={false} label="Session details" />;

	const meta = detail?.metadata ?? {};
	const sys = meta?.sessionMetadata?.systemData ?? {};
	const addr = meta?.sessionMetadata?.userAddress ?? {};
	const devInfo = meta?.deviceInfo ?? {};


	return (
		<LightboxCtx.Provider value={openZoom}>
		<Drawer open={open} label="Session details">
			<DrawerHead
				title={session.name || session.email || 'Session'}
				onClose={onClose}
				chip={
					<>
						{verdict ? (
							<StatusChip verdict={verdict.verdict}>{verdict.label}</StatusChip>
						) : null}
						{/* The compliance officer's own read, next to the machine's. Only
							shown once someone has actually set it — an "unassigned" pill
							would just be noise on every session that has not been triaged. */}
						{riskLevel ? (
							<span className={`rpill rpill--${riskLevel.value}`}>
								{riskLevel.label} risk
							</span>
						) : null}
					</>
				}
			>
				<div className="dsteps">
					{chain.map(step => (
						<span key={step.key}>
							<i className={DOT[step.state] ?? 'dot-pend'} />
							{step.label} ({STATE_WORD[step.state] ?? step.state}) &nbsp;
						</span>
					))}
				</div>
				<div className="dbtns">
					<button className="dbtn pri" type="button" onClick={() => onViewFlow(session)}>
						Tree Flow
					</button>
					{/* Three formats now that pipelines.api builds them: pdf renders the
						details page, csv/xlsx build from the session data (lib/tabular.ts).
						A menu rather than three buttons — the head is already tight. */}
					<div className="dlmenu">
						<button
							className="dbtn"
							type="button"
							onClick={() => setDlOpen(o => !o)}
							aria-expanded={dlOpen}
						>
							Download ▾
						</button>
						{dlOpen && (
							<div className="dlmenu__list">
								{DOWNLOAD_FORMATS.map(f => (
									<button
										key={f.value}
										type="button"
										className="dlmenu__item"
										onClick={() => {
											setDlOpen(false);
											onDownload(session, f.value);
										}}
									>
										{f.label}
									</button>
								))}
							</div>
						)}
					</div>
				</div>
				<div className="dleg">
					Status &amp; Flow Legends:
					<span className="vch ns">NOT STARTED</span>
					<span className="vch pur">STARTED</span>
					<span className="vch rev">UNDER REVIEW</span>
					<span className="vch ok">APPROVED</span>
					<span className="vch rej">REJECTED</span>
				</div>
			</DrawerHead>

			{!loaded && <div className="sv2-loading">Loading session details…</div>}

			{lightbox && (
				<div
					className="lbx"
					role="button"
					tabIndex={0}
					aria-label="Close enlarged document"
					onClick={() => setLightbox(null)}
					onKeyDown={e => {
						if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ')
							setLightbox(null);
					}}
				>
					{/* SIZED INLINE, and the width lives on the element rather than in
						the stylesheet. The frame is a shrink-to-fit flex column, so the
						player's intrinsic width (405px — barely bigger than the
						thumbnail this was opened from) settles the layout before a
						declared width on the container gets a say. A `:has()` selector
						and then a modifier class both compiled, both matched, and both
						still lost that argument; an inline width is not in it. */}
					<div
						className={`lbx__inner${
							lightbox.kind === 'video' ? ' lbx__inner--video' : ''
						}`}
						style={
							lightbox.kind === 'video'
								? { width: 'min(880px, 90vw)' }
								: undefined
						}
						onClick={e => e.stopPropagation()}
					>
						<div className="lbx__bar">
							{lightbox.label}
							<button
								type="button"
								className="lbx__x"
								onClick={() => setLightbox(null)}
								aria-label="Close"
							>
								&#10005;
							</button>
						</div>
						{lightbox.kind === 'video' ? (
							/* Keyed on the url and autoplayed: the reviewer opened this to
							   watch it, and a signed url that has been refreshed must
							   remount rather than replay a stale source. */
							<video
								key={lightbox.src}
								className="lbx__video"
								controls
								autoPlay
								loop
								playsInline
							>
								<source src={lightbox.src} type="video/mp4" />
								<source src={lightbox.src} type="video/webm" />
							</video>
						) : (
							<img src={lightbox.src} alt={lightbox.label} />
						)}
					</div>
				</div>
			)}

			{/* Risk — the compliance agent's own read on the session, independent of
				any step's verdict. Same PATCH /activities the log is built from, so
				the change shows up in Activity below with its before/after. */}
			<Section
				title="Risk"
				right={
					currentRisk && currentRisk !== 'unassigned'
						? `Currently : ${currentRisk}`
						: 'Unassigned'
				}
			>
				<div className="risk">
					{RISK_LEVELS.map(lvl => (
						<button
							key={lvl.value}
							type="button"
							className={`risk__opt risk__opt--${lvl.value}${
								currentRisk === lvl.value ? ' is-on' : ''
							}`}
							disabled={posting || currentRisk === lvl.value}
							onClick={() => setRisk(lvl.value, currentRisk)}
						>
							{currentRisk === lvl.value && (
								<i className="risk__tick" aria-hidden="true">
									&#10003;
								</i>
							)}
							{lvl.label}
						</button>
					))}
				</div>
			</Section>

			{/* Assignment — the old details page could hand a session to a named
				reviewer (use-session-assign.ts); the sv2 drawer dropped it, so the
				only way left was the list's bulk action. The current assignee reads
				from the session itself so the drawer says who owns it even before
				anything is changed. */}
			<Section
				title="Assignment"
				right={
					assignedTo ? `Currently : ${assignedTo}` : 'Unassigned'
				}
			>
				<div className="asgn">
					<select
						className="asgn__sel"
						value={assignee}
						disabled={posting}
						onChange={e => setAssignee(e.target.value)}
					>
						<option value="">Select a reviewer…</option>
						{reviewers.map((r: any) => (
							<option key={r._id} value={r._id}>
								{[r.firstName, r.lastName].filter(Boolean).join(' ') || r.email}
							</option>
						))}
					</select>
					{/* POST /session-assignee validates `comment` as a REQUIRED,
						non-empty string — omitting it answers 400 "comment is
						required" and sending "" answers 400 "not allowed to be
						empty". It is a handover note, so the button stays disabled
						until there is one rather than inventing filler. */}
					<input
						className="asgn__note"
						value={assignNote}
						disabled={posting}
						placeholder="Note for the reviewer (required)"
						onChange={e => setAssignNote(e.target.value)}
					/>
					<button
						className="bact"
						type="button"
						disabled={posting || !assignee || !assignNote.trim()}
						onClick={async () => {
							const ok = await assign(assignee, assignNote.trim());
							if (ok) {
								setAssignee('');
								setAssignNote('');
							}
						}}
					>
						{posting ? 'Assigning…' : 'Assign'}
					</button>
				</div>
			</Section>

			<Section
				title="Session Information"
				right={`ID : ${session._id}${meta?.deviceInfo?.type ? ` · ${meta.deviceInfo.type}` : ''}`}
			>
				<KV
					rows={[
						['Name', session.name || null],
						['Email', session.email || null],
						[
							'Phone',
							session.contact
								? `${session.countryCode ?? ''} ${session.contact}`.trim()
								: <span className="gy">NA</span>,
						],
						['Device OS', [sys?.os?.name, sys?.os?.version].filter(Boolean).join(' ') || null],
						['Device', [sys?.device?.vendor, sys?.device?.model].filter(Boolean).join(' ') || null],
						['User IP', meta?.sessionMetadata?.ipAddress ?? null],
						['Region', addr?.region ?? null],
						['Browser', [sys?.browser?.name, sys?.browser?.major].filter(Boolean).join(' ') || null],
						['City', addr?.city ?? null],
						['Country Code', addr?.country_code ?? null],
						/* Device ID and Apple ID are only ever populated by the native
						   apps — a desktop web session carries them as empty strings.
						   They are printed as NA rather than dropped (the way KV drops
						   an empty row) because "no device id was captured" is itself
						   the answer a reviewer is looking for; a missing row reads as
						   "we forgot to show it". */
						[
							'Device ID',
							devInfo?.deviceId || <span className="gy">NA</span>,
						],
						[
							'Apple ID',
							devInfo?.appleId || <span className="gy">NA</span>,
						],
						['Created', dayTime(session.createdAt)],
						['Updated', session.updatedAt ? dayTime(session.updatedAt) : null],
					]}
				/>
				{/* The map belongs to the device/location block above it, so it sits
					inside Session Information rather than in a section of its own. */}
				<SessionMap geo={meta?.geoLocation} />
			</Section>

			{steps.authentication && (
				<BiometricSection
					payload={steps.authentication}
					similarity={similarity}
				/>
			)}

			{steps.dynamicForm && (
				<DynamicFormSection
					payload={steps.dynamicForm}
					action={stepAction('dynamicForm')}
					title="Dynamic Form"
				/>
			)}
			{steps.form && (
				<DynamicFormSection
					payload={steps.form}
					action={stepAction('form')}
					title="Questionnaire"
				/>
			)}
			{steps.kyc && <KycSection payload={steps.kyc} action={stepAction('kyc')} />}
			{/* Directly under KYC — it is bought on that step and searched on that
				step's own result — but as its own section, because a criminal record
				is not a KYC verdict. */}
			{steps.kyc && <CriminalCheckSection payload={steps.kyc} />}
			{/* One block PER NODE: a flow can carry two accreditation steps at
				different levels, and each is its own packet with its own status. */}
			{(stepsAll.accreditation ?? []).map((payload: any, i: number) => (
				<AccreditationSection
					key={val(payload?._id) ?? String(i)}
					payload={payload}
					action={stepAction('accreditation', val(payload?._id) ?? undefined)}
					onResend={resendAccreditationInvite}
				/>
			))}
			{steps.kyb && (
				<KybSection
					payload={steps.kyb}
					action={stepAction('kyb')}
					ranKyc={!!steps.kyc}
					ranAml={!!steps.aml}
				/>
			)}
			{/* Directly under KYB — bought on that step and run on that step's own
				roster — but as its own section, because a sanctions hit on a
				beneficial owner is not a KYB registry field. */}
			{steps.kyb && <MembersOfacSection payload={steps.kyb} />}
			{steps.signAgreement && (
				<SignAgreementSection
					payload={steps.signAgreement}
					action={stepAction('signAgreement')}
				/>
			)}
			{steps.aml && <AmlSection payload={steps.aml} action={stepAction('aml')} />}

			<Section title="Activity">
				<div className="acttabs">
					<span
						className={`ktab${actTab === 'all' ? ' on' : ''}`}
						onClick={() => setActTab('all')}
					>
						All Activity
					</span>
					<span
						className={`ktab${actTab === 'comments' ? ' on' : ''}`}
						onClick={() => setActTab('comments')}
					>
						Only Comments
					</span>
				</div>

				{activities.length === 0 && loaded && (
					<div className="sv2-empty">No activity recorded yet.</div>
				)}

				{activities.map(a => {
					const m = a?.metadata ?? {};
					// the old page hid a no-op status write the same way
					if (a.type === 'status' && m.from === m.to) return null;
					return (
						<div className="act" key={a.id}>
							<b>{a.userName || 'System'}</b>{' '}
							{a.type === 'status' ? (
								<>
									has changed the status of <b>{stepLabel(m.stepId)}</b> from{' '}
									{statusChip(m.from)} to {statusChip(m.to)}
								</>
							) : m.priorityRisk ? (
								/* A risk change is posted as a COMMENT carrying priorityRisk
								   with an empty `data`, so without this it rendered as a bare
								   "X commented" with nothing after it. */
								<>
									has set the risk to {riskChip(m.priorityRisk)}
									{m.previousPriorityRisk &&
									m.previousPriorityRisk !== 'unassigned' ? (
										<> from {riskChip(m.previousPriorityRisk)}</>
									) : null}
								</>
							) : a.type === 'comment' ? (
								'commented'
							) : (
								m.data || 'updated the session'
							)}
							{/* the note is its own element so it can be coloured apart from
								the system activity around it — what a person typed should be
								findable at a glance in a log that is mostly machine events */}
							{m.data && (a.type === 'comment' || a.type === 'status') ? (
								<>
									{a.type === 'comment' ? ': ' : ' — '}
									<span className="act__cmt">{m.data}</span>
								</>
							) : null}
							<span className="at">{dayTime(m.time)}</span>
						</div>
					);
				})}

				<div className="cmt">
					<textarea
						placeholder="Enter your comment..."
						value={text}
						onChange={e => setText(e.target.value)}
					/>
					<div className="opts">
						<label>
							<input
								type="checkbox"
								checked={sendMail}
								onChange={e => setSendMail(e.target.checked)}
							/>
							Send as Mail
						</label>
						<button
							className="bact"
							type="button"
							style={{ marginLeft: 'auto' }}
							disabled={posting || !text.trim()}
							onClick={submit}
						>
							{posting ? 'Posting…' : 'Comment'}
						</button>
					</div>
				</div>
			</Section>
		</Drawer>
		</LightboxCtx.Provider>
	);
};
